import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  Shield, 
  Building2, 
  Users, 
  TrendingUp,
  Plus,
  LogOut,
  Copy,
  Eye,
  Trash2,
  Calendar,
  BarChart3,
  CheckCircle,
  AlertCircle,
  Mail,
  X,
  Zap,
  FolderOpen,
  Edit,
  Crown,
  Lock,
  Unlock,
  Award,
  Save,
  Clock,
  User,
  Target,
  Flag,
  HardDrive,
  CheckSquare,
  Tag,
  FileText,
  Briefcase,
  Repeat
} from 'lucide-react';
import InputWithVariablePicker from './InputWithVariablePicker';
import SubtaskTemplateModal from './SubtaskTemplateModal';
import TaskTemplateSelector from './TaskTemplateSelector';
import SecurityAuditLogs from './SecurityAuditLogs';
import LockedAccounts from './LockedAccounts';
import ErrorLogs from './ErrorLogs';
import RecurringTaskModal from './RecurringTaskModal';
import WorkflowTemplateBuilder from './WorkflowTemplateBuilder';
import { DollarSign, CreditCard } from 'lucide-react';


// Billing & Revenue View Component
const BillingRevenueView = () => {
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState(null);
  const [newSignups, setNewSignups] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signupDays, setSignupDays] = useState(30);

  useEffect(() => {
    fetchBillingData();
  }, [signupDays]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('super_admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [analyticsRes, signupsRes, orgsRes] = await Promise.all([
        api.get(`/super-admin/subscription-analytics`, { headers }),
        api.get(`/super-admin/new-signups?days=${signupDays}`, { headers }),
        api.get(`/super-admin/organizations?limit=100`, { headers })
      ]);

      if (analyticsRes.data.success) setSubscriptionAnalytics(analyticsRes.data);
      if (signupsRes.data.success) setNewSignups(signupsRes.data.users);
      if (orgsRes.data.success) setAllOrganizations(orgsRes.data.organizations);
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: { bg: '#dcfce7', text: '#166534' },
      past_due: { bg: '#fef3c7', text: '#92400e' },
      locked: { bg: '#fee2e2', text: '#991b1b' },
      canceled: { bg: '#f3f4f6', text: '#374151' },
      alpha: { bg: '#dbeafe', text: '#1e40af' },
      inactive: { bg: '#f3f4f6', text: '#6b7280' }
    };
    const color = colors[status] || colors.inactive;
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '600',
        backgroundColor: color.bg,
        color: color.text,
        textTransform: 'capitalize'
      }}>
        {status || 'Unknown'}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ width: '2rem', height: '2rem', border: '3px solid #e5e7eb', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading billing data...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Revenue Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={20} style={{ color: '#16a34a' }} />
            </div>
            <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>MRR</span>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
            {formatCurrency(subscriptionAnalytics?.mrr || 0)}
          </p>
        </div>

        <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCard size={20} style={{ color: '#2563eb' }} />
            </div>
            <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>Total Revenue</span>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
            {formatCurrency(subscriptionAnalytics?.total_revenue || 0)}
          </p>
        </div>

        <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', backgroundColor: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Building2 size={20} style={{ color: '#7c3aed' }} />
            </div>
            <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>Active Subscriptions</span>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
            {subscriptionAnalytics?.active_subscriptions || 0}
          </p>
        </div>

        <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', backgroundColor: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={20} style={{ color: '#db2777' }} />
            </div>
            <span style={{ color: '#4b5563', fontSize: '0.875rem' }}>New This Month</span>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
            {subscriptionAnalytics?.new_subscriptions_this_month || 0}
          </p>
        </div>
      </div>

      {/* Subscription Status Breakdown */}
      {subscriptionAnalytics?.status_breakdown && (
        <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>Subscription Status Breakdown</h3>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            {Object.entries(subscriptionAnalytics.status_breakdown).map(([status, count]) => (
              <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {getStatusBadge(status)}
                <span style={{ fontWeight: '600', color: '#111827' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Signups */}
      <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
            New Signups ({newSignups.length})
          </h3>
          <select 
            value={signupDays} 
            onChange={(e) => setSignupDays(parseInt(e.target.value))}
            style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>User</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Team</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Signed Up</th>
              </tr>
            </thead>
            <tbody>
              {newSignups.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No new signups in this period</td>
                </tr>
              ) : (
                newSignups.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div>
                        <p style={{ fontWeight: '500', color: '#111827' }}>{user.name}</p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>{user.email}</p>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', color: '#374151' }}>{user.team_name || 'No team'}</td>
                    <td style={{ padding: '0.75rem' }}>{getStatusBadge(user.subscription_status)}</td>
                    <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.875rem' }}>{formatDate(user.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* All Organizations with Payment Info */}
      <div style={{ background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
          All Organizations - Payment Status
        </h3>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Organization</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Owner</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Billing</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Members</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Lifetime Value</th>
              </tr>
            </thead>
            <tbody>
              {allOrganizations.map((org) => (
                <tr key={org.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.75rem' }}>
                    <p style={{ fontWeight: '500', color: '#111827' }}>{org.name}</p>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Created {formatDate(org.created_at)}</p>
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <p style={{ color: '#374151' }}>{org.owner_name || 'N/A'}</p>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>{org.owner_email}</p>
                  </td>
                  <td style={{ padding: '0.75rem' }}>{getStatusBadge(org.subscription_status)}</td>
                  <td style={{ padding: '0.75rem', color: '#374151', textTransform: 'capitalize' }}>{org.billing_cycle || 'N/A'}</td>
                  <td style={{ padding: '0.75rem', color: '#374151' }}>
                    {org.member_count} / {org.max_seats || '∞'}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                    {formatCurrency(org.total_revenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Styles object - defined outside component to be accessible to all nested components
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f9fafb 0%, #f3e8ff 50%, #fce7f3 100%)',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    padding: '1rem 2rem',
    boxShadow: '0 4px 15px -3px rgba(147, 51, 234, 0.3)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  headerContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: 'white',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subtitle: {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.8)'
  },
  userInfo: {
    textAlign: 'right'
  },
  userName: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#111827'
  },
  userRole: {
    fontSize: '0.75rem',
    color: '#6b7280'
  },
  logoutBtn: {
    padding: '0.5rem',
    color: 'rgba(255, 255, 255, 0.8)',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    borderRadius: '0.375rem',
    transition: 'color 0.2s'
  },
  layoutWrapper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  sidebar: {
    width: '260px',
    backgroundColor: 'white',
    borderRight: '1px solid #e5e7eb',
    padding: '1.5rem 1rem',
    overflowY: 'auto',
    flexShrink: 0,
    boxShadow: '2px 0 10px -3px rgba(0, 0, 0, 0.1)'
  },
  sidebarTitle: {
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
    paddingLeft: '0.75rem'
  },
  main: {
    flex: 1,
    padding: '2rem',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 72px)'
  },
  message: {
    marginBottom: '1.5rem',
    padding: '1rem',
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  messageSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0'
  },
  messageError: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: '1px solid #fecaca'
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    transition: 'all 0.2s',
    cursor: 'pointer',
    border: 'none',
    fontSize: '0.875rem',
    textAlign: 'left',
    width: '100%'
  },
  navButtonActive: {
    backgroundColor: '#7c3aed',
    color: 'white',
    boxShadow: '0 2px 8px -2px rgba(124, 58, 237, 0.5)'
  },
  navButtonInactive: {
    color: '#374151',
    backgroundColor: 'transparent'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem'
  },
  statCard: {
    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
    backdropFilter: 'blur(10px)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '1rem',
    padding: '1.5rem',
    border: '1px solid rgba(147, 51, 234, 0.2)',
    boxShadow: '0 4px 6px -1px rgba(147, 51, 234, 0.1)'
  },
  statHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  statIcon: {
    padding: '0.5rem',
    borderRadius: '0.5rem',
    marginRight: '1rem'
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#111827'
  },
  statLabel: {
    fontSize: '0.875rem',
    color: '#4b5563'
  },
  statMeta: {
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    color: '#4b5563'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  cardHeader: {
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  cardTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#111827'
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(147, 51, 234, 0.3)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  tableHead: {
    backgroundColor: '#f9fafb'
  },
  th: {
    padding: '0.75rem 1.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  td: {
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '0.875rem'
  },
  badge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    boxShadow: '0 2px 4px rgba(147, 51, 234, 0.3)'
  },
  badgeGreen: {
    backgroundColor: '#dcfce7',
    color: '#166534'
  },
  badgeYellow: {
    backgroundColor: '#fef3c7',
    color: '#d97706'
  },
  actionButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.75rem',
    borderRadius: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    transition: 'all 0.2s',
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    color: 'white',
    fontWeight: '600',
    boxShadow: '0 2px 4px rgba(147, 51, 234, 0.3)'
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  sectionHeader: {
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#111827'
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'border-color 0.2s'
  }
};

// Email Stats View Component
const EmailStatsView = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [resettingTeam, setResettingTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [allTeams, setAllTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [teamStatsLoading, setTeamStatsLoading] = useState(false);

  useEffect(() => {
    fetchEmailStats();
    fetchAllTeams();
  }, [days]);

  const fetchEmailStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/email/stats?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch email stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTeams = async () => {
    setTeamsLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/email/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAllTeams(response.data.teams);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setTeamsLoading(false);
    }
  };

  const fetchTeamStats = async (teamId) => {
    setTeamStatsLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/email/team/${teamId}/stats?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTeamStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch team stats:', error);
    } finally {
      setTeamStatsLoading(false);
    }
  };

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    fetchTeamStats(team.id);
  };

  const handleResetSuspension = async (teamId) => {
    setResettingTeam(teamId);
    try {
      const token = localStorage.getItem('super_admin_token');
      await api.post(`/super-admin/email/reset-suspension/${teamId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmailStats();
      fetchAllTeams();
      if (selectedTeam && selectedTeam.id === teamId) {
        fetchTeamStats(teamId);
      }
    } catch (error) {
      console.error('Failed to reset suspension:', error);
    } finally {
      setResettingTeam(null);
    }
  };

  // Filter teams based on search query
  const filteredTeams = allTeams.filter(team => 
    team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem'
  };

  const statBoxStyle = {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center'
  };

  // Team Detail View
  if (selectedTeam) {
    return (
      <div>
        {/* Back Button */}
        <button
          onClick={() => { setSelectedTeam(null); setTeamStats(null); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#f3f4f6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '1.5rem',
            fontSize: '0.875rem',
            color: '#374151'
          }}
        >
          ← Back to Overview
        </button>

        {/* Team Header */}
        <div style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '0.25rem' }}>
              {selectedTeam.name}
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{selectedTeam.id}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {selectedTeam.email_suspended ? (
              <>
                <span style={{
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontWeight: '500'
                }}>
                  ⚠️ Email Suspended
                </span>
                <button
                  onClick={() => handleResetSuspension(selectedTeam.id)}
                  disabled={resettingTeam === selectedTeam.id}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {resettingTeam === selectedTeam.id ? 'Resetting...' : 'Reset Suspension'}
                </button>
              </>
            ) : (
              <span style={{
                backgroundColor: '#ecfdf5',
                color: '#059669',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: '500'
              }}>
                ✓ Active
              </span>
            )}
            <select
              value={days}
              onChange={(e) => { setDays(parseInt(e.target.value)); }}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white'
              }}
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {/* Team Rate Limits */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Rate Limits</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
            <div style={statBoxStyle}>
              <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>{selectedTeam.email_limit_per_minute || 100}</p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Per Minute</p>
            </div>
            <div style={statBoxStyle}>
              <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>{selectedTeam.email_limit_per_hour || 1000}</p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Per Hour</p>
            </div>
            <div style={statBoxStyle}>
              <p style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>{selectedTeam.email_limit_per_day || 5000}</p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Per Day</p>
            </div>
          </div>
        </div>

        {teamStatsLoading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ 
              width: '40px', height: '40px', border: '3px solid #e5e7eb',
              borderTopColor: '#6366f1', borderRadius: '50%',
              animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
            }} />
            <p style={{ color: '#6b7280' }}>Loading team statistics...</p>
          </div>
        ) : teamStats ? (
          <>
            {/* Team Overall Stats */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
                Email Statistics ({days} day{days > 1 ? 's' : ''})
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '1rem' }}>
                <div style={statBoxStyle}>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{teamStats.overall.total.toLocaleString()}</p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total</p>
                </div>
                <div style={{ ...statBoxStyle, backgroundColor: '#ecfdf5' }}>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#059669' }}>{teamStats.overall.sent.toLocaleString()}</p>
                  <p style={{ fontSize: '0.75rem', color: '#059669' }}>Sent</p>
                </div>
                <div style={{ ...statBoxStyle, backgroundColor: '#fef3c7' }}>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#d97706' }}>{teamStats.overall.pending.toLocaleString()}</p>
                  <p style={{ fontSize: '0.75rem', color: '#d97706' }}>Pending</p>
                </div>
                <div style={{ ...statBoxStyle, backgroundColor: '#fee2e2' }}>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc2626' }}>{teamStats.overall.failed.toLocaleString()}</p>
                  <p style={{ fontSize: '0.75rem', color: '#dc2626' }}>Failed</p>
                </div>
                <div style={statBoxStyle}>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>{teamStats.overall.unique_recipients.toLocaleString()}</p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Recipients</p>
                </div>
              </div>
            </div>

            {/* Team Daily Breakdown */}
            {teamStats.daily && teamStats.daily.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Daily Breakdown</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#059669' }}>Sent</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>Failed</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Success Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamStats.daily.map((day, index) => {
                        const successRate = day.total > 0 ? ((day.sent / day.total) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.75rem', color: '#111827' }}>{day.date}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', color: '#111827' }}>{day.total.toLocaleString()}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>{day.sent.toLocaleString()}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>{day.failed.toLocaleString()}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              <span style={{
                                backgroundColor: parseFloat(successRate) >= 95 ? '#ecfdf5' : parseFloat(successRate) >= 80 ? '#fef3c7' : '#fee2e2',
                                color: parseFloat(successRate) >= 95 ? '#059669' : parseFloat(successRate) >= 80 ? '#d97706' : '#dc2626',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}>
                                {successRate}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Team Recent Emails */}
            {teamStats.recent_emails && teamStats.recent_emails.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Recent Emails</h3>
                <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f9fafb' }}>
                      <tr>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Recipient</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Subject</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Status</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamStats.recent_emails.map((email, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.75rem', color: '#111827' }}>{email.recipient}</td>
                          <td style={{ padding: '0.75rem', color: '#6b7280', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {email.subject}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <span style={{
                              backgroundColor: email.status === 'sent' ? '#ecfdf5' : email.status === 'pending' ? '#fef3c7' : '#fee2e2',
                              color: email.status === 'sent' ? '#059669' : email.status === 'pending' ? '#d97706' : '#dc2626',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>
                              {email.status}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', color: '#6b7280', fontSize: '0.75rem' }}>
                            {new Date(email.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Team Gateway Events */}
            {teamStats.events && teamStats.events.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Gateway Events</h3>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {teamStats.events.map((event, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '0.75rem',
                      borderBottom: index < teamStats.events.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: event.event_type === 'cap_breach' ? '#dc2626' : '#059669',
                        marginTop: '0.5rem',
                        flexShrink: 0
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{
                            backgroundColor: event.event_type === 'cap_breach' ? '#fee2e2' : '#ecfdf5',
                            color: event.event_type === 'cap_breach' ? '#dc2626' : '#059669',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}>
                            {event.event_type.replace('_', ' ')}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {new Date(event.created_at).toLocaleString()}
                          </span>
                        </div>
                        {event.details && (
                          <pre style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            marginTop: '0.25rem',
                            backgroundColor: '#f9fafb',
                            padding: '0.5rem',
                            borderRadius: '4px'
                          }}>
                            {JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    );
  }

  // Main Overview View
  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ 
          width: '40px', height: '40px', border: '3px solid #e5e7eb',
          borderTopColor: '#6366f1', borderRadius: '50%',
          animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
        }} />
        <p style={{ color: '#6b7280' }}>Loading email statistics...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <AlertCircle size={48} style={{ color: '#ef4444', margin: '0 auto 1rem' }} />
        <p style={{ color: '#ef4444' }}>Failed to load email statistics</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Period Selector */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>Email Gateway Statistics</h2>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            backgroundColor: 'white',
            fontSize: '0.875rem'
          }}
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Team Search */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
          🔍 Find Team
        </h3>
        <input
          type="text"
          placeholder="Search by team name or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            fontSize: '0.875rem',
            marginBottom: '1rem'
          }}
        />
        {searchQuery && (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredTeams.length === 0 ? (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '1rem' }}>No teams found</p>
            ) : (
              filteredTeams.slice(0, 20).map((team) => (
                <div
                  key={team.id}
                  onClick={() => handleSelectTeam(team)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: '#f9fafb',
                    marginBottom: '0.5rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                >
                  <div>
                    <p style={{ fontWeight: '500', color: '#111827' }}>{team.name}</p>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>{team.id}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {team.email_suspended && (
                      <span style={{
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>
                        Suspended
                      </span>
                    )}
                    <span style={{ color: '#6366f1', fontSize: '0.875rem' }}>View →</span>
                  </div>
                </div>
              ))
            )}
            {filteredTeams.length > 20 && (
              <p style={{ color: '#6b7280', textAlign: 'center', fontSize: '0.75rem', padding: '0.5rem' }}>
                Showing 20 of {filteredTeams.length} results. Refine your search.
              </p>
            )}
          </div>
        )}
        {!searchQuery && allTeams.length > 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            {allTeams.length} teams available. Start typing to search.
          </p>
        )}
      </div>

      {/* Overall Stats */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
          Overall Statistics ({days} day{days > 1 ? 's' : ''})
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' }}>
          <div style={statBoxStyle}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{stats.overall.total_emails.toLocaleString()}</p>
            <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total Emails</p>
          </div>
          <div style={{ ...statBoxStyle, backgroundColor: '#ecfdf5' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#059669' }}>{stats.overall.sent.toLocaleString()}</p>
            <p style={{ fontSize: '0.75rem', color: '#059669' }}>Sent</p>
          </div>
          <div style={{ ...statBoxStyle, backgroundColor: '#fef3c7' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#d97706' }}>{stats.overall.pending.toLocaleString()}</p>
            <p style={{ fontSize: '0.75rem', color: '#d97706' }}>Pending</p>
          </div>
          <div style={{ ...statBoxStyle, backgroundColor: '#fee2e2' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc2626' }}>{stats.overall.failed.toLocaleString()}</p>
            <p style={{ fontSize: '0.75rem', color: '#dc2626' }}>Failed</p>
          </div>
          <div style={{ ...statBoxStyle, backgroundColor: '#fef2f2' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#991b1b' }}>{stats.overall.dead_letter.toLocaleString()}</p>
            <p style={{ fontSize: '0.75rem', color: '#991b1b' }}>Dead Letter</p>
          </div>
          <div style={statBoxStyle}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6366f1' }}>{stats.overall.unique_tenants}</p>
            <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Active Tenants</p>
          </div>
          <div style={statBoxStyle}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>{stats.overall.unique_recipients.toLocaleString()}</p>
            <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Unique Recipients</p>
          </div>
          <div style={{ ...statBoxStyle, backgroundColor: stats.overall.suspended_teams > 0 ? '#fee2e2' : '#f9fafb' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: stats.overall.suspended_teams > 0 ? '#dc2626' : '#111827' }}>
              {stats.overall.suspended_teams}
            </p>
            <p style={{ fontSize: '0.75rem', color: stats.overall.suspended_teams > 0 ? '#dc2626' : '#6b7280' }}>Suspended Teams</p>
          </div>
        </div>
      </div>

      {/* Daily Breakdown */}
      {stats.daily && stats.daily.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Daily Breakdown</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#059669' }}>Sent</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>Failed</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {stats.daily.map((day, index) => {
                  const successRate = day.total > 0 ? ((day.sent / day.total) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', color: '#111827' }}>{day.date}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: '#111827' }}>{day.total.toLocaleString()}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>{day.sent.toLocaleString()}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>{day.failed.toLocaleString()}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <span style={{
                          backgroundColor: parseFloat(successRate) >= 95 ? '#ecfdf5' : parseFloat(successRate) >= 80 ? '#fef3c7' : '#fee2e2',
                          color: parseFloat(successRate) >= 95 ? '#059669' : parseFloat(successRate) >= 80 ? '#d97706' : '#dc2626',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          {successRate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Sending Tenants */}
      {stats.top_tenants && stats.top_tenants.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Top Sending Tenants</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Team</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Total</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#059669' }}>Sent</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>Failed</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Daily Limit</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_tenants.map((tenant, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem', color: '#111827' }}>
                      <div style={{ fontWeight: '500' }}>{tenant.team_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{tenant.tenant_id}</div>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: '#111827', fontWeight: '600' }}>
                      {tenant.email_count.toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: '#059669' }}>{tenant.sent_count.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: '#dc2626' }}>{tenant.failed_count.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: '#6b7280' }}>{tenant.daily_limit.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {tenant.suspended ? (
                        <span style={{
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          Suspended
                        </span>
                      ) : (
                        <span style={{
                          backgroundColor: '#ecfdf5',
                          color: '#059669',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          Active
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                        <button
                          onClick={() => {
                            const team = allTeams.find(t => t.id === tenant.tenant_id) || {
                              id: tenant.tenant_id,
                              name: tenant.team_name,
                              email_suspended: tenant.suspended,
                              email_limit_per_day: tenant.daily_limit
                            };
                            handleSelectTeam(team);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          View
                        </button>
                        {tenant.suspended && (
                          <button
                            onClick={() => handleResetSuspension(tenant.tenant_id)}
                            disabled={resettingTeam === tenant.tenant_id}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              opacity: resettingTeam === tenant.tenant_id ? 0.5 : 1
                            }}
                          >
                            {resettingTeam === tenant.tenant_id ? '...' : 'Reset'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Events */}
      {stats.recent_events && stats.recent_events.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Recent Gateway Events</h3>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {stats.recent_events.map((event, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '0.75rem',
                borderBottom: index < stats.recent_events.length - 1 ? '1px solid #e5e7eb' : 'none'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: event.event_type === 'cap_breach' ? '#dc2626' : 
                                   event.event_type === 'suspension_reset' ? '#059669' : '#6366f1',
                  marginTop: '0.5rem',
                  flexShrink: 0
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <span 
                        style={{ fontWeight: '500', color: '#6366f1', cursor: 'pointer' }}
                        onClick={() => {
                          const team = allTeams.find(t => t.id === event.tenant_id) || {
                            id: event.tenant_id,
                            name: event.team_name
                          };
                          handleSelectTeam(team);
                        }}
                      >
                        {event.team_name}
                      </span>
                      <span style={{
                        marginLeft: '0.5rem',
                        backgroundColor: event.event_type === 'cap_breach' ? '#fee2e2' : '#e0e7ff',
                        color: event.event_type === 'cap_breach' ? '#dc2626' : '#4338ca',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>
                        {event.event_type.replace('_', ' ')}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {new Date(event.created_at).toLocaleString()}
                    </span>
                  </div>
                  {event.details && (
                    <pre style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      marginTop: '0.25rem',
                      backgroundColor: '#f9fafb',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(event.details, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Usage (Rate Limits) */}
      {stats.current_usage && stats.current_usage.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>Current Rate Limit Usage (Today)</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Team</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Minute</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Hour</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Day</th>
                </tr>
              </thead>
              <tbody>
                {stats.current_usage.map((usage, index) => {
                  const minutePct = (usage.minute_count / usage.limits.per_minute) * 100;
                  const hourPct = (usage.hour_count / usage.limits.per_hour) * 100;
                  const dayPct = (usage.day_count / usage.limits.per_day) * 100;
                  
                  const getColor = (pct) => pct >= 90 ? '#dc2626' : pct >= 70 ? '#d97706' : '#059669';
                  
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', color: '#111827' }}>
                        <span 
                          style={{ cursor: 'pointer', color: '#6366f1' }}
                          onClick={() => {
                            const team = allTeams.find(t => t.id === usage.tenant_id) || {
                              id: usage.tenant_id,
                              name: usage.team_name
                            };
                            handleSelectTeam(team);
                          }}
                        >
                          {usage.team_name}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <span style={{ color: getColor(minutePct) }}>
                          {usage.minute_count}/{usage.limits.per_minute}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <span style={{ color: getColor(hourPct) }}>
                          {usage.hour_count}/{usage.limits.per_hour}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <span style={{ color: getColor(dayPct) }}>
                          {usage.day_count}/{usage.limits.per_day}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SuperAdminDashboard = ({ superAdmin, onLogout }) => {
  const [currentView, setCurrentView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [showDeletedOrgs, setShowDeletedOrgs] = useState(false);
  const [deletedOrganizations, setDeletedOrganizations] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [aiUsageStats, setAiUsageStats] = useState(null);
  const [aiUsageTab, setAiUsageTab] = useState('organization'); // 'organization' or 'operation'
  const [storageUsageStats, setStorageUsageStats] = useState(null);
  const [selectedTeamStorage, setSelectedTeamStorage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  
  // Organization search and profile state
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [selectedOrgProfile, setSelectedOrgProfile] = useState(null);
  const [orgProfileData, setOrgProfileData] = useState(null);
  const [orgProfileLoading, setOrgProfileLoading] = useState(false);
  
  // Subscription management state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({
    subscription_status: '',
    subscription_tier: '',
    max_seats: '',
    white_label_enabled: false
  });

  // Helper function to format estimated effort display
  const formatEstimatedEffort = (effort, metric) => {
    if (!effort) return '';
    const metricLabel = metric === 'minutes' ? 'min' : metric === 'hours' ? 'hr' : metric === 'days' ? 'd' : 'hr';
    return `${effort}${metricLabel}`;
  };

  // Filter organizations based on search query
  const filteredOrganizations = (showDeletedOrgs ? deletedOrganizations : organizations).filter(org => {
    if (!orgSearchQuery) return true;
    const query = orgSearchQuery.toLowerCase();
    return (
      org.organization_name?.toLowerCase().includes(query) ||
      org.owner_email?.toLowerCase().includes(query) ||
      org.owner_name?.toLowerCase().includes(query)
    );
  });

  // Fetch organization profile data
  const fetchOrgProfile = async (orgId) => {
    setOrgProfileLoading(true);
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/organization/${orgId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setOrgProfileData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch org profile:', error);
    } finally {
      setOrgProfileLoading(false);
    }
  };

  const handleViewOrgProfile = (org) => {
    setSelectedOrgProfile(org);
    fetchOrgProfile(org.id);
  };

  const handleCloseOrgProfile = () => {
    setSelectedOrgProfile(null);
    setOrgProfileData(null);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (showDeletedOrgs) {
      fetchDeletedOrganizations();
    } else {
      fetchOrganizations();
    }
  }, [showDeletedOrgs]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchAnalytics(), 
        fetchOrganizations(), 
        fetchDeletedOrganizations(),
        fetchErrorLogs(),
        fetchAiUsageStats(),
        fetchStorageUsageStats()
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAnalytics(response.data.analytics);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setOrganizations(response.data.organizations);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const fetchDeletedOrganizations = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/deleted-teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedOrganizations(response.data.teams || []);
    } catch (error) {
      console.error('Failed to fetch deleted organizations:', error);
    }
  };

  const fetchErrorLogs = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/error-logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setErrorLogs(response.data.error_logs);
      }
    } catch (error) {
      console.error('Failed to fetch error logs:', error);
    }
  };

  const fetchAiUsageStats = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/ai-usage?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAiUsageStats(response.data.ai_usage);
      }
    } catch (error) {
      console.error('Failed to fetch AI usage stats:', error);
    }
  };

  const fetchStorageUsageStats = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/storage-usage?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStorageUsageStats(response.data.storage_usage);
      }
    } catch (error) {
      console.error('Failed to fetch storage usage stats:', error);
    }
  };

  const fetchTeamStorageDetails = async (teamId) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.get(`/super-admin/storage-usage/${teamId}?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSelectedTeamStorage(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch team storage details:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin_info');
    onLogout();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', content: 'Copied to clipboard!' });
      setTimeout(() => setMessage({ type: '', content: '' }), 3000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Subscription Management Functions
  const openSubscriptionModal = (org) => {
    setSelectedOrganization(org);
    setSubscriptionForm({
      subscription_status: org.subscription_status || 'inactive',
      subscription_tier: org.subscription_tier || 'starter',
      max_seats: org.max_seats || '',
      white_label_enabled: org.white_label_enabled || false
    });
    setShowSubscriptionModal(true);
  };

  const handleDeleteOrganization = async (org) => {
    if (!window.confirm(`Are you sure you want to delete "${org.organization_name}"?\n\nThis organization can be restored later from the Deleted Organizations section.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('super_admin_token');
      await api.delete(`/super-admin/teams/${org.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', content: 'Organization deleted successfully. You can restore it from the Deleted Organizations section.' });
      fetchOrganizations(); // Refresh the list
    } catch (error) {
      console.error('Error deleting organization:', error);
      setMessage({ type: 'error', content: `Failed to delete organization: ${error.response?.data?.detail || error.message}` });
    }
  };

  const handleRestoreOrganization = async (org) => {
    if (!window.confirm(`Restore "${org.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('super_admin_token');
      await api.post(`/super-admin/teams/${org.id}/restore`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', content: 'Organization restored successfully!' });
      fetchDeletedOrganizations(); // Refresh deleted orgs list
      fetchOrganizations(); // Refresh active orgs list
    } catch (error) {
      console.error('Error restoring organization:', error);
      setMessage({ type: 'error', content: `Failed to restore organization: ${error.response?.data?.detail || error.message}` });
    }
  };

  const handleSetAlpha = async (teamId, teamName) => {
    if (!window.confirm(`Set "${teamName}" to Alpha status? This will grant unrestricted access.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.post(
        `/super-admin/teams/${teamId}/set-alpha`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', content: `✅ ${teamName} set to Alpha status` });
        fetchOrganizations(); // Refresh list
      }
    } catch (error) {
      console.error('Failed to set alpha:', error);
      setMessage({ type: 'error', content: 'Failed to set Alpha status' });
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const updateData = {
        subscription_status: subscriptionForm.subscription_status,
        subscription_tier: subscriptionForm.subscription_tier,
        max_seats: subscriptionForm.max_seats === '' ? null : parseInt(subscriptionForm.max_seats),
        white_label_enabled: subscriptionForm.white_label_enabled
      };

      const response = await api.put(
        `/super-admin/teams/${selectedOrganization.id}/subscription`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({ type: 'success', content: '✅ Subscription updated successfully' });
        setShowSubscriptionModal(false);
        fetchOrganizations(); // Refresh list
      }
    } catch (error) {
      console.error('Failed to update subscription:', error);
      setMessage({ type: 'error', content: 'Failed to update subscription' });
    }
  };

  const getSubscriptionBadgeStyle = (status) => {
    const baseStyle = { ...styles.badge };
    switch(status) {
      case 'alpha':
        return { 
          ...baseStyle, 
          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          color: 'white',
          fontWeight: '700',
          boxShadow: '0 2px 4px rgba(251, 191, 36, 0.3)'
        };
      case 'active':
        return { 
          ...baseStyle, 
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
        };
      case 'trial':
        return { 
          ...baseStyle, 
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          color: 'white',
          boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
        };
      case 'past_due':
        return { 
          ...baseStyle, 
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          color: 'white',
          boxShadow: '0 2px 4px rgba(249, 115, 22, 0.3)'
        };
      case 'canceled':
      case 'inactive':
        return { 
          ...baseStyle, 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
        };
      default:
        return { 
          ...baseStyle, 
          background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
          color: 'white',
          boxShadow: '0 2px 4px rgba(156, 163, 175, 0.3)'
        };
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #e5e7eb',
          borderRadius: '50%',
          borderTopColor: '#7c3aed',
          animation: 'spin 1s ease-in-out infinite'
        }}></div>
        <style>
          {`@keyframes spin { to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  // Subscription Management Modal
  const SubscriptionModal = () => {
    if (!showSubscriptionModal || !selectedOrganization) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          padding: '2rem',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
              Manage Subscription: {selectedOrganization.organization_name}
            </h3>
            <button
              onClick={() => setShowSubscriptionModal(false)}
              style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}
            >
              ×
            </button>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Subscription Status
            </label>
            <select
              value={subscriptionForm.subscription_status}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_status: e.target.value })}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
            >
              <option value="alpha">Alpha (Unrestricted)</option>
              <option value="active">Active (Paid)</option>
              <option value="trial">Trial</option>
              <option value="past_due">Past Due</option>
              <option value="canceled">Canceled</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Subscription Tier
            </label>
            <select
              value={subscriptionForm.subscription_tier}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_tier: e.target.value })}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
            >
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Max Seats (Leave empty for unlimited)
            </label>
            <input
              type="number"
              value={subscriptionForm.max_seats}
              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, max_seats: e.target.value })}
              placeholder="Unlimited"
              min="1"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none'
              }}
            />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              Current usage: {selectedOrganization.total_users} seats
            </p>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={subscriptionForm.white_label_enabled}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, white_label_enabled: e.target.checked })}
                style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Enable White-Label Features
              </span>
            </label>
          </div>

          {selectedOrganization.stripe_customer_id && (
            <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '0.375rem', marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                <strong>Stripe Customer ID:</strong> {selectedOrganization.stripe_customer_id}
              </p>
              {selectedOrganization.stripe_subscription_id && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <strong>Stripe Subscription ID:</strong> {selectedOrganization.stripe_subscription_id}
                </p>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowSubscriptionModal(false)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6b7280',
                color: 'white',
                borderRadius: '0.375rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateSubscription}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#7c3aed',
                color: 'white',
                borderRadius: '0.375rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Update Subscription
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <Shield size={32} style={{ color: 'white' }} />
            <div>
              <h1 style={styles.title}>Athena Super Admin</h1>
              <p style={styles.subtitle}>Platform Management Console</p>
            </div>
          </div>
          
          <div style={styles.headerRight}>
            <div style={styles.userInfo}>
              <p style={{ ...styles.userName, color: 'white', fontWeight: '600' }}>{superAdmin.username}</p>
              <p style={{ ...styles.userRole, color: 'rgba(255, 255, 255, 0.9)' }}>Super Administrator</p>
            </div>
            <button
              onClick={handleLogout}
              style={styles.logoutBtn}
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div style={styles.layoutWrapper}>
        {/* Sidebar Navigation */}
        <aside style={styles.sidebar}>
          <p style={styles.sidebarTitle}>Navigation</p>
          <div style={styles.nav}>
            {[
              { id: 'overview', name: 'Overview', icon: BarChart3 },
              { id: 'organizations', name: 'Organizations', icon: Building2 },
              { id: 'billing', name: 'Billing & Revenue', icon: TrendingUp },
              { id: 'template-library', name: 'Template Library', icon: FolderOpen },
              { id: 'users', name: 'Users', icon: Users },
              { id: 'email-stats', name: 'Email Stats', icon: Mail },
              { id: 'ai-usage', name: 'AI Token Usage', icon: Zap },
              { id: 'storage-usage', name: 'Storage Usage', icon: HardDrive },
              { id: 'error-logs', name: 'Error Logs', icon: AlertCircle },
              { id: 'locked-accounts', name: 'Locked Accounts', icon: Lock },
              { id: 'security-audit', name: 'Security Audit', icon: Shield }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCurrentView(tab.id)}
                style={{
                  ...styles.navButton,
                  ...(currentView === tab.id ? styles.navButtonActive : styles.navButtonInactive)
                }}
              >
                <tab.icon size={18} />
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <div style={styles.main}>
          {/* Message */}
          {message.content && (
            <div style={{
              ...styles.message,
              ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
            }}>
              <CheckCircle size={20} />
              <span>{message.content}</span>
            </div>
          )}

        {/* Overview */}
        {currentView === 'overview' && analytics && (
          <div>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statHeader}>
                  <div style={{ ...styles.statIcon, backgroundColor: '#dbeafe' }}>
                    <Building2 size={24} style={{ color: '#2563eb' }} />
                  </div>
                  <div>
                    <p style={styles.statValue}>{analytics.organizations.total}</p>
                    <p style={styles.statLabel}>Alpha Organizations</p>
                  </div>
                </div>
                <div style={styles.statMeta}>
                  <span style={{ color: '#059669', fontWeight: '500' }}>
                    {analytics.organizations.active} Active
                  </span>
                  <span style={{ margin: '0 0.5rem', color: '#9ca3af' }}>•</span>
                  <span style={{ color: '#d97706' }}>
                    {analytics.organizations.pending} Pending
                  </span>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statHeader}>
                  <div style={{ ...styles.statIcon, backgroundColor: '#dcfce7' }}>
                    <Users size={24} style={{ color: '#059669' }} />
                  </div>
                  <div>
                    <p style={styles.statValue}>{analytics.users.total}</p>
                    <p style={styles.statLabel}>Total Users</p>
                  </div>
                </div>
                <div style={styles.statMeta}>
                  <span style={{ color: '#4b5563' }}>
                    +{analytics.users.recent_week} this week
                  </span>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statHeader}>
                  <div style={{ ...styles.statIcon, backgroundColor: '#fce7f3' }}>
                    <TrendingUp size={24} style={{ color: '#be185d' }} />
                  </div>
                  <div>
                    <p style={styles.statValue}>{analytics.clients.total}</p>
                    <p style={styles.statLabel}>Total Clients</p>
                  </div>
                </div>
                <div style={styles.statMeta}>
                  <span style={{ color: '#4b5563' }}>
                    Avg: {analytics.clients.average_per_org} per org
                  </span>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statHeader}>
                  <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7' }}>
                    <Zap size={24} style={{ color: '#d97706' }} />
                  </div>
                  <div>
                    <p style={styles.statValue}>
                      {analytics.ai_usage?.total_tokens ? 
                        (analytics.ai_usage.total_tokens > 1000000 ? 
                          `${(analytics.ai_usage.total_tokens / 1000000).toFixed(1)}M` : 
                          `${(analytics.ai_usage.total_tokens / 1000).toFixed(0)}K`) 
                        : '0'
                      }
                    </p>
                    <p style={styles.statLabel}>AI Tokens Used</p>
                  </div>
                </div>
                <div style={styles.statMeta}>
                  <span style={{ color: '#4b5563' }}>
                    ${analytics.ai_usage?.total_cost || '0.00'} total cost
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing & Revenue */}
        {currentView === 'billing' && (
          <BillingRevenueView />
        )}

        {/* Organizations */}
        {currentView === 'organizations' && (
          <div>
            {/* Organization Profile View */}
            {selectedOrgProfile ? (
              <div>
                {/* Back Button */}
                <button
                  onClick={handleCloseOrgProfile}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    marginBottom: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#374151'
                  }}
                >
                  ← Back to Organizations
                </button>

                {orgProfileLoading ? (
                  <div style={{ padding: '3rem', textAlign: 'center' }}>
                    <div style={{ 
                      width: '40px', height: '40px', border: '3px solid #e5e7eb',
                      borderTopColor: '#6366f1', borderRadius: '50%',
                      animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
                    }} />
                    <p style={{ color: '#6b7280' }}>Loading organization profile...</p>
                  </div>
                ) : orgProfileData ? (
                  <>
                    {/* Organization Header */}
                    <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                            {orgProfileData.organization.name}
                          </h2>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              Owner: <strong>{orgProfileData.organization.owner_name}</strong> ({orgProfileData.organization.owner_email})
                            </span>
                            <span style={getSubscriptionBadgeStyle(orgProfileData.organization.subscription_status)}>
                              {orgProfileData.organization.subscription_status === 'alpha' && <Crown size={12} style={{ display: 'inline', marginRight: '4px' }} />}
                              {orgProfileData.organization.subscription_status?.toUpperCase() || 'INACTIVE'}
                            </span>
                            {orgProfileData.organization.white_label_enabled && (
                              <span style={{ backgroundColor: '#ecfdf5', color: '#059669', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                                <Award size={12} style={{ display: 'inline', marginRight: '4px' }} /> White-Label
                              </span>
                            )}
                            {orgProfileData.organization.email_suspended && (
                              <span style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                                ⚠️ Email Suspended
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                            Created: {new Date(orgProfileData.organization.created_at).toLocaleDateString()} • ID: {orgProfileData.organization.id}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => openSubscriptionModal(selectedOrgProfile)}
                            style={{ ...styles.button, backgroundColor: '#6366f1' }}
                          >
                            <Edit size={16} /> Edit Subscription
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                      {/* Users */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <Users size={20} style={{ color: '#6366f1' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>Users</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.users.total.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.users.owners} owners&nbsp;&nbsp;•&nbsp;&nbsp;{orgProfileData.stats.users.admins} admins&nbsp;&nbsp;•&nbsp;&nbsp;{orgProfileData.stats.users.members} members
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.375rem' }}>
                          Max seats: {orgProfileData.organization.max_seats || '∞'}
                        </p>
                      </div>

                      {/* Projects */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <Briefcase size={20} style={{ color: '#059669' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>Projects</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.projects.total.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.projects.active} active&nbsp;&nbsp;•&nbsp;&nbsp;{orgProfileData.stats.projects.completed} completed
                        </p>
                      </div>

                      {/* Tasks */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <CheckSquare size={20} style={{ color: '#d97706' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>Tasks</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.tasks.total.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.tasks.completed} completed&nbsp;&nbsp;•&nbsp;&nbsp;{orgProfileData.stats.tasks.open} open
                        </p>
                        {orgProfileData.stats.tasks.overdue > 0 && (
                          <p style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.375rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <AlertCircle size={12} /> {orgProfileData.stats.tasks.overdue} overdue
                          </p>
                        )}
                      </div>

                      {/* Clients */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <Building2 size={20} style={{ color: '#8b5cf6' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>Clients</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.clients.total.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.clients.active} active
                        </p>
                      </div>

                      {/* AI Usage */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <Zap size={20} style={{ color: '#f59e0b' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>AI Usage (30d)</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.ai_usage.total_tokens === 0 
                            ? '0'
                            : orgProfileData.stats.ai_usage.total_tokens > 1000000 
                              ? `${(orgProfileData.stats.ai_usage.total_tokens / 1000000).toFixed(1)}M`
                              : orgProfileData.stats.ai_usage.total_tokens > 1000
                                ? `${(orgProfileData.stats.ai_usage.total_tokens / 1000).toFixed(1)}K`
                                : orgProfileData.stats.ai_usage.total_tokens.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.ai_usage.total_requests.toLocaleString()} requests&nbsp;&nbsp;•&nbsp;&nbsp;${orgProfileData.stats.ai_usage.total_cost.toFixed(2)}
                        </p>
                      </div>

                      {/* Storage */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <HardDrive size={20} style={{ color: '#0ea5e9' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>Storage</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.storage.total_mb > 1000 
                            ? `${(orgProfileData.stats.storage.total_mb / 1000).toFixed(1)} GB`
                            : `${Math.round(orgProfileData.stats.storage.total_mb)} MB`}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.storage.total_files.toLocaleString()} files
                        </p>
                      </div>

                      {/* Emails */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <Mail size={20} style={{ color: '#ec4899' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>Emails (30d)</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.emails.total.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.emails.sent} sent&nbsp;&nbsp;•&nbsp;&nbsp;{orgProfileData.stats.emails.failed} failed
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.375rem' }}>
                          Limits: {orgProfileData.organization.email_limits.per_minute.toLocaleString()}/min, {orgProfileData.organization.email_limits.per_day.toLocaleString()}/day
                        </p>
                      </div>

                      {/* Workflows */}
                      <div style={styles.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <Repeat size={20} style={{ color: '#14b8a6' }} />
                          <span style={{ fontWeight: '600', color: '#374151' }}>Workflows</span>
                        </div>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
                          {orgProfileData.stats.workflows.total.toLocaleString()}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {orgProfileData.stats.workflows.active} active
                        </p>
                      </div>
                    </div>

                    {/* Activity */}
                    <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
                      <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>Activity (Last 7 Days)</h3>
                      <div style={{ display: 'flex', gap: '2rem' }}>
                        <div>
                          <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{orgProfileData.stats.activity.active_days_last_week}</p>
                          <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Active days</p>
                        </div>
                        <div>
                          <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{orgProfileData.stats.activity.total_actions_last_week}</p>
                          <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total actions</p>
                        </div>
                      </div>
                    </div>

                    {/* Users List */}
                    <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
                      <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>Team Members ({orgProfileData.users.length})</h3>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f9fafb' }}>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Name</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Email</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Role</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Last Login</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Joined</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orgProfileData.users.map((user, index) => (
                              <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <td style={{ padding: '0.75rem', color: '#111827' }}>{user.name}</td>
                                <td style={{ padding: '0.75rem', color: '#6b7280' }}>{user.email}</td>
                                <td style={{ padding: '0.75rem' }}>
                                  <span style={{
                                    backgroundColor: user.role === 'owner' ? '#fef3c7' : user.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                                    color: user.role === 'owner' ? '#92400e' : user.role === 'admin' ? '#1e40af' : '#374151',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                  }}>
                                    {user.role}
                                  </span>
                                </td>
                                <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.75rem' }}>
                                  {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                                </td>
                                <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.75rem' }}>
                                  {user.joined_at ? new Date(user.joined_at).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Recent Projects */}
                    {orgProfileData.recent_projects.length > 0 && (
                      <div style={styles.card}>
                        <h3 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>Recent Projects</h3>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f9fafb' }}>
                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Project</th>
                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                                <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Tasks</th>
                                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Created</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orgProfileData.recent_projects.map((project) => (
                                <tr key={project.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '0.75rem', color: '#111827', fontWeight: '500' }}>{project.name}</td>
                                  <td style={{ padding: '0.75rem' }}>
                                    <span style={{
                                      backgroundColor: project.status === 'active' ? '#ecfdf5' : project.status === 'completed' ? '#dbeafe' : '#f3f4f6',
                                      color: project.status === 'active' ? '#059669' : project.status === 'completed' ? '#2563eb' : '#6b7280',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem'
                                    }}>
                                      {project.status}
                                    </span>
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', color: '#6b7280' }}>{project.task_count}</td>
                                  <td style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.75rem' }}>
                                    {new Date(project.created_at).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            ) : (
              /* Organizations List View */
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>Alpha Organizations ({filteredOrganizations.length})</h2>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={() => setShowDeletedOrgs(!showDeletedOrgs)}
                      style={{
                        ...styles.actionButton,
                        backgroundColor: showDeletedOrgs ? '#3b82f6' : '#6b7280',
                        color: 'white',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                      {showDeletedOrgs ? 'Show Active Organizations' : 'Show Deleted Organizations'}
                    </button>
                    {!showDeletedOrgs && (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        style={styles.button}
                      >
                        <Plus size={16} />
                        <span>Add Organization</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Search Box */}
                <div style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    placeholder="Search by organization name or owner email..."
                    value={orgSearchQuery}
                    onChange={(e) => setOrgSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.875rem'
                    }}
                  />
                  {orgSearchQuery && (
                    <span style={{ marginLeft: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                      {filteredOrganizations.length} result{filteredOrganizations.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                
                {filteredOrganizations.length === 0 ? (
                  <div style={{ padding: '3rem', textAlign: 'center' }}>
                    <Building2 size={48} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                    <p style={{ color: '#6b7280' }}>
                      {orgSearchQuery 
                        ? 'No organizations match your search' 
                        : (showDeletedOrgs ? 'No deleted organizations' : 'No organizations created yet')}
                    </p>
                  </div>
                ) : (
                  <table style={styles.table}>
                    <thead style={styles.tableHead}>
                      <tr>
                        <th style={styles.th}>Organization</th>
                        <th style={styles.th}>Owner</th>
                        <th style={styles.th}>Subscription</th>
                        <th style={styles.th}>Seats</th>
                        <th style={styles.th}>Features</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrganizations.map((org) => (
                        <tr key={org.id}>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <Building2 size={16} style={{ color: '#9ca3af' }} />
                              <div>
                                <p 
                                  style={{ fontWeight: '500', color: '#6366f1', cursor: 'pointer' }}
                                  onClick={() => handleViewOrgProfile(org)}
                                >
                                  {org.organization_name}
                                </p>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  {org.total_clients} clients • {org.total_projects} projects
                                </p>
                              </div>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div>
                              <p style={{ color: '#111827' }}>{org.owner_name}</p>
                              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {org.owner_email}
                              </p>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div>
                              <span style={getSubscriptionBadgeStyle(org.subscription_status)}>
                                {org.subscription_status === 'alpha' && <Crown size={12} style={{ display: 'inline', marginRight: '4px' }} />}
                                {org.subscription_status ? org.subscription_status.toUpperCase() : 'INACTIVE'}
                              </span>
                              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                                {org.subscription_tier || 'starter'}
                              </p>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={{ fontSize: '0.875rem', color: '#111827' }}>
                              {org.total_users} / {org.max_seats || '∞'}
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {org.max_seats ? `${org.max_seats - org.total_users} remaining` : 'Unlimited'}
                            </p>
                          </td>
                          <td style={styles.td}>
                            {org.white_label_enabled ? (
                              <span style={{ fontSize: '0.75rem', color: '#166534', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Award size={14} /> White-Label
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Standard</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {showDeletedOrgs ? (
                                <>
                                  <button
                                    onClick={() => handleRestoreOrganization(org)}
                                    style={{
                                      ...styles.actionButton,
                                      backgroundColor: '#10b981',
                                      color: 'white'
                                    }}
                                    title="Restore Organization"
                                  >
                                    <Unlock size={16} /> Restore
                                  </button>
                                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
                                    Deleted {new Date(org.deleted_at).toLocaleDateString()}
                                    {org.deleted_by && ` by ${org.deleted_by}`}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => openSubscriptionModal(org)}
                                    style={styles.actionButton}
                                    title="Edit subscription"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  {org.subscription_status !== 'alpha' && (
                                    <button
                                      onClick={() => handleSetAlpha(org.id, org.organization_name)}
                                      style={{ ...styles.actionButton, color: '#d97706' }}
                                      title="Set to Alpha"
                                    >
                                      <Crown size={16} />
                                    </button>
                                  )}
                                  <button 
                                    onClick={() => handleViewOrgProfile(org)}
                                    style={styles.actionButton} 
                                    title="View details"
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteOrganization(org)}
                                    style={{
                                      ...styles.actionButton,
                                      backgroundColor: '#ef4444',
                                      color: 'white'
                                    }}
                                    title="Delete Organization"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* AI Usage Tab */}
        {currentView === 'ai-usage' && (
          <div className="space-y-6">
            <h2 style={styles.cardTitle}>AI Token Usage Analytics</h2>
            
            {/* Summary Stats */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statHeader}>
                  <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7' }}>
                    <Zap size={24} style={{ color: '#d97706' }} />
                  </div>
                  <div>
                    <p style={styles.statValue}>
                      {aiUsageStats?.summary?.total_tokens ? 
                        (aiUsageStats.summary.total_tokens > 1000000 ? 
                          `${(aiUsageStats.summary.total_tokens / 1000000).toFixed(1)}M` : 
                          `${(aiUsageStats.summary.total_tokens / 1000).toFixed(0)}K`) 
                        : (analytics?.ai_usage?.total_tokens ? 
                          (analytics.ai_usage.total_tokens > 1000000 ? 
                            `${(analytics.ai_usage.total_tokens / 1000000).toFixed(1)}M` : 
                            `${(analytics.ai_usage.total_tokens / 1000).toFixed(0)}K`) 
                          : '0')
                      }
                    </p>
                    <p style={styles.statLabel}>Total Tokens (30 days)</p>
                  </div>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statHeader}>
                  <div style={{ ...styles.statIcon, backgroundColor: '#dcfce7' }}>
                    <TrendingUp size={24} style={{ color: '#059669' }} />
                  </div>
                  <div>
                    <p style={styles.statValue}>
                      ${aiUsageStats?.summary?.total_cost?.toFixed(2) || analytics?.ai_usage?.total_cost || '0.00'}
                    </p>
                    <p style={styles.statLabel}>Total Cost (30 days)</p>
                  </div>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.statHeader}>
                  <div style={{ ...styles.statIcon, backgroundColor: '#dbeafe' }}>
                    <BarChart3 size={24} style={{ color: '#2563eb' }} />
                  </div>
                  <div>
                    <p style={styles.statValue}>
                      {aiUsageStats?.summary?.total_requests || analytics?.ai_usage?.total_requests || 0}
                    </p>
                    <p style={styles.statLabel}>API Requests</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-tabs for Organization vs Operation */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                onClick={() => setAiUsageTab('organization')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  backgroundColor: aiUsageTab === 'organization' ? '#7c3aed' : '#e5e7eb',
                  color: aiUsageTab === 'organization' ? 'white' : '#374151'
                }}
              >
                <Building2 size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                By Organization
              </button>
              <button
                onClick={() => setAiUsageTab('operation')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  backgroundColor: aiUsageTab === 'operation' ? '#7c3aed' : '#e5e7eb',
                  color: aiUsageTab === 'operation' ? 'white' : '#374151'
                }}
              >
                <Zap size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                By Operation Type
              </button>
              <button
                onClick={() => setAiUsageTab('model')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  backgroundColor: aiUsageTab === 'model' ? '#7c3aed' : '#e5e7eb',
                  color: aiUsageTab === 'model' ? 'white' : '#374151'
                }}
              >
                <BarChart3 size={16} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                By Model
              </button>
            </div>

            {/* Usage by Organization */}
            {aiUsageTab === 'organization' && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>AI Usage by Organization</h3>
                  <button
                    onClick={fetchAiUsageStats}
                    style={{ ...styles.button, backgroundColor: '#6b7280' }}
                  >
                    Refresh
                  </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Organization</th>
                        <th style={styles.th}>Requests</th>
                        <th style={styles.th}>Total Tokens</th>
                        <th style={styles.th}>Cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiUsageStats?.usage_by_team?.length > 0 ? (
                        aiUsageStats.usage_by_team.map((team, index) => (
                          <tr key={index}>
                            <td style={styles.td}>
                              <span style={{ fontWeight: '500' }}>{team.team_name}</span>
                            </td>
                            <td style={styles.td}>{team.request_count?.toLocaleString() || 0}</td>
                            <td style={styles.td}>
                              {team.total_tokens > 1000 
                                ? `${(team.total_tokens / 1000).toFixed(1)}K` 
                                : team.total_tokens || 0}
                            </td>
                            <td style={styles.td}>
                              <span style={{ 
                                color: team.total_cost_usd > 10 ? '#dc2626' : 
                                       team.total_cost_usd > 1 ? '#f59e0b' : '#10b981',
                                fontWeight: '600'
                              }}>
                                ${team.total_cost_usd?.toFixed(4) || '0.0000'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" style={{ ...styles.td, textAlign: 'center', padding: '2rem' }}>
                            No AI usage data by organization yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Usage by Operation Type */}
            {aiUsageTab === 'operation' && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>Usage by Operation Type</h3>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  {(aiUsageStats?.usage_by_operation?.length > 0 || analytics?.ai_usage?.by_operation?.length > 0) ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      {(aiUsageStats?.usage_by_operation || analytics?.ai_usage?.by_operation || []).map((op, index) => (
                        <div key={index} style={styles.statCard}>
                          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem', textTransform: 'capitalize' }}>
                            {op.operation_type || op._id || 'Unknown'}
                          </h4>
                          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#7c3aed' }}>
                            {(op.total_tokens || op.tokens) > 1000 
                              ? `${((op.total_tokens || op.tokens) / 1000).toFixed(1)}K` 
                              : (op.total_tokens || op.tokens || 0)}
                          </p>
                          <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {op.request_count || op.requests || 0} requests
                          </p>
                          {op.total_cost_usd && (
                            <p style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                              ${op.total_cost_usd.toFixed(4)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                      No AI usage data available yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Usage by Model */}
            {aiUsageTab === 'model' && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>Usage by AI Model</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Model</th>
                        <th style={styles.th}>Requests</th>
                        <th style={styles.th}>Total Tokens</th>
                        <th style={styles.th}>Cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiUsageStats?.usage_by_model?.length > 0 ? (
                        aiUsageStats.usage_by_model.map((model, index) => (
                          <tr key={index}>
                            <td style={styles.td}>
                              <span style={{ fontWeight: '500', fontFamily: 'monospace' }}>{model.model}</span>
                            </td>
                            <td style={styles.td}>{model.request_count?.toLocaleString() || 0}</td>
                            <td style={styles.td}>
                              {model.total_tokens > 1000 
                                ? `${(model.total_tokens / 1000).toFixed(1)}K` 
                                : model.total_tokens || 0}
                            </td>
                            <td style={styles.td}>
                              <span style={{ color: '#059669', fontWeight: '600' }}>
                                ${model.total_cost_usd?.toFixed(4) || '0.0000'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" style={{ ...styles.td, textAlign: 'center', padding: '2rem' }}>
                            No AI usage data by model yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Storage Usage Tab */}
        {currentView === 'storage-usage' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <HardDrive size={20} color="#7c3aed" />
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Total Storage</span>
                </div>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                  {storageUsageStats?.summary?.total_storage_formatted || '0 B'}
                </p>
              </div>
              <div style={styles.statCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <FolderOpen size={20} color="#10b981" />
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Total Files</span>
                </div>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                  {storageUsageStats?.summary?.total_files?.toLocaleString() || 0}
                </p>
              </div>
              <div style={styles.statCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <Building2 size={20} color="#3b82f6" />
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Organizations with Storage</span>
                </div>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                  {storageUsageStats?.summary?.organizations_with_storage || 0}
                </p>
              </div>
            </div>

            {/* Storage by Organization */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Storage by Organization</h2>
                <button
                  onClick={fetchStorageUsageStats}
                  style={{ ...styles.button, backgroundColor: '#6b7280' }}
                >
                  Refresh
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Organization</th>
                      <th style={styles.th}>Total Storage</th>
                      <th style={styles.th}>File Count</th>
                      <th style={styles.th}>Last Updated</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storageUsageStats?.storage_by_organization?.length > 0 ? (
                      storageUsageStats.storage_by_organization.map((org) => (
                        <tr key={org.team_id}>
                          <td style={styles.td}>
                            <span style={{ fontWeight: '500' }}>{org.team_name}</span>
                          </td>
                          <td style={styles.td}>
                            <span style={{ 
                              color: org.total_bytes > 1073741824 ? '#dc2626' : 
                                     org.total_bytes > 104857600 ? '#f59e0b' : '#10b981',
                              fontWeight: '600'
                            }}>
                              {org.total_formatted}
                            </span>
                          </td>
                          <td style={styles.td}>{org.file_count?.toLocaleString() || 0}</td>
                          <td style={styles.td}>
                            {org.last_updated ? new Date(org.last_updated).toLocaleDateString() : 'N/A'}
                          </td>
                          <td style={styles.td}>
                            <button
                              onClick={() => fetchTeamStorageDetails(org.team_id)}
                              style={{ ...styles.button, padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            >
                              <Eye size={14} /> Details
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ ...styles.td, textAlign: 'center', padding: '2rem' }}>
                          No storage data available yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Storage by File Type */}
            {storageUsageStats?.storage_by_file_type?.length > 0 && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>Storage by File Type (Last 30 Days)</h3>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                    {storageUsageStats.storage_by_file_type.map((type, index) => (
                      <div key={index} style={styles.statCard}>
                        <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                          {type.file_type?.split('/').pop() || 'Unknown'}
                        </h4>
                        <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#7c3aed' }}>
                          {type.total_formatted}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {type.file_count} files
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Team Storage Details Modal */}
            {selectedTeamStorage && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', zIndex: 50
              }}>
                <div style={{
                  backgroundColor: 'white', borderRadius: '0.75rem', padding: '1.5rem',
                  maxWidth: '800px', width: '90%', maxHeight: '80vh', overflow: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                      Storage Details: {selectedTeamStorage.team?.name}
                    </h3>
                    <button onClick={() => setSelectedTeamStorage(null)} style={{ padding: '0.5rem' }}>
                      <X size={20} />
                    </button>
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={styles.statCard}>
                        <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>Total Storage</span>
                        <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#7c3aed' }}>
                          {selectedTeamStorage.summary?.total_formatted || '0 B'}
                        </p>
                      </div>
                      <div style={styles.statCard}>
                        <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>File Count</span>
                        <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#10b981' }}>
                          {selectedTeamStorage.summary?.file_count || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedTeamStorage.storage_by_user?.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Usage by User</h4>
                      <table style={{ ...styles.table, fontSize: '0.875rem' }}>
                        <thead>
                          <tr>
                            <th style={styles.th}>User</th>
                            <th style={styles.th}>Uploads</th>
                            <th style={styles.th}>Total Uploaded</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTeamStorage.storage_by_user.map((user, idx) => (
                            <tr key={idx}>
                              <td style={styles.td}>{user.user_email || user.user_name || user.user_id}</td>
                              <td style={styles.td}>{user.uploads}</td>
                              <td style={styles.td}>{user.bytes_uploaded_formatted}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {selectedTeamStorage.recent_files?.length > 0 && (
                    <div>
                      <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Recent Files</h4>
                      <table style={{ ...styles.table, fontSize: '0.75rem' }}>
                        <thead>
                          <tr>
                            <th style={styles.th}>File</th>
                            <th style={styles.th}>Size</th>
                            <th style={styles.th}>Type</th>
                            <th style={styles.th}>Operation</th>
                            <th style={styles.th}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTeamStorage.recent_files.slice(0, 20).map((file, idx) => (
                            <tr key={idx}>
                              <td style={{ ...styles.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {file.file_path?.split('/').pop() || file.file_path}
                              </td>
                              <td style={styles.td}>{file.file_size_formatted}</td>
                              <td style={styles.td}>{file.file_type?.split('/').pop() || 'unknown'}</td>
                              <td style={styles.td}>
                                <span style={{
                                  padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.625rem',
                                  backgroundColor: file.operation === 'upload' ? '#dcfce7' : '#fee2e2',
                                  color: file.operation === 'upload' ? '#166534' : '#991b1b'
                                }}>
                                  {file.operation}
                                </span>
                              </td>
                              <td style={styles.td}>{new Date(file.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Logs Tab */}
        {currentView === 'error-logs' && (
          <ErrorLogs superAdminToken={localStorage.getItem('super_admin_token')} />
        )}

        {/* Template Library View */}
        {currentView === 'template-library' && (
          <TemplateLibraryView />
        )}

        {/* Users View */}
        {currentView === 'users' && (
          <UsersView />
        )}

        {/* Email Stats View */}
        {currentView === 'email-stats' && (
          <EmailStatsView />
        )}

        {/* Locked Accounts View */}
        {currentView === 'locked-accounts' && (
          <LockedAccounts superAdminToken={localStorage.getItem('super_admin_token')} />
        )}

        {currentView === 'security-audit' && (
          <SecurityAuditLogs superAdminToken={localStorage.getItem('super_admin_token')} />
        )}
        </div>
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <CreateOrgModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchOrganizations();
            setMessage({ type: 'success', content: 'Organization created successfully!' });
          }}
        />
      )}

      {/* Subscription Management Modal */}
      <SubscriptionModal />
    </div>
  );
};

// Create Organization Modal
const CreateOrgModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    organization_name: '',
    owner_name: '',
    owner_email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('super_admin_token');
      const response = await api.post(`/super-admin/organizations`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccessData(response.data);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (successData) {
      onSuccess();
    }
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      padding: '1.5rem',
      width: '100%',
      maxWidth: '400px',
      margin: '1rem'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem'
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: '#111827'
    },
    closeBtn: {
      color: '#6b7280',
      cursor: 'pointer',
      border: 'none',
      background: 'none',
      fontSize: '1.5rem'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem'
    },
    input: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem'
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.75rem',
      paddingTop: '1rem'
    },
    cancelBtn: {
      flex: 1,
      padding: '0.5rem 1rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    submitBtn: {
      flex: 1,
      padding: '0.5rem 1rem',
      backgroundColor: '#7c3aed',
      color: 'white',
      borderRadius: '0.375rem',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: '500'
    },
    error: {
      marginBottom: '1rem',
      padding: '0.75rem',
      backgroundColor: '#fee2e2',
      border: '1px solid #fecaca',
      borderRadius: '0.375rem',
      color: '#dc2626',
      fontSize: '0.875rem'
    }
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>
            {successData ? 'Organization Created!' : 'Add Alpha Organization'}
          </h3>
          <button onClick={handleClose} style={modalStyles.closeBtn}>×</button>
        </div>

        {successData ? (
          <div>
            <div style={{
              padding: '1rem',
              backgroundColor: '#d1fae5',
              border: '1px solid #6ee7b7',
              borderRadius: '0.375rem',
              marginBottom: '1rem'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#065f46', marginBottom: '0.5rem' }}>
                ✅ {successData.message}
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                Organization Details
              </h4>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                <p><strong>Email:</strong> {successData.owner_email}</p>
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '0.375rem',
              marginBottom: '1rem'
            }}>
              <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#92400e', marginBottom: '0.5rem' }}>
                ⚠️ Temporary Password (Save This!)
              </p>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.5rem',
                backgroundColor: 'white',
                borderRadius: '0.25rem',
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}>
                <code style={{ flex: 1 }}>{successData.temporary_password}</code>
                <button
                  onClick={() => copyToClipboard(successData.temporary_password)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem'
                  }}
                >
                  Copy
                </button>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.5rem' }}>
                Owner should change this password on first login
              </p>
            </div>

            <button
              onClick={handleClose}
              style={{
                width: '100%',
                padding: '0.5rem 1rem',
                backgroundColor: '#7c3aed',
                color: 'white',
                borderRadius: '0.375rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {error && <div style={modalStyles.error}>{error}</div>}

            <form onSubmit={handleSubmit} style={modalStyles.form}>
              <div>
                <label style={modalStyles.label}>Organization Name</label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
                  style={modalStyles.input}
                  placeholder="Enter organization name"
                  required
                />
              </div>

              <div>
                <label style={modalStyles.label}>Owner Name</label>
                <input
                  type="text"
                  value={formData.owner_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                  style={modalStyles.input}
                  placeholder="Enter owner name"
                  required
                />
              </div>

              <div>
                <label style={modalStyles.label}>Owner Email</label>
                <input
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner_email: e.target.value }))}
                  style={modalStyles.input}
                  placeholder="Enter owner email"
                  required
                />
              </div>

              <div style={modalStyles.buttonGroup}>
                <button
                  type="button"
                  onClick={onClose}
                  style={modalStyles.cancelBtn}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...modalStyles.submitBtn,
                    ...(loading ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                  }}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

  // Template Library View
  const TemplateLibraryView = () => {
    const [activeTab, setActiveTab] = useState('projects'); // 'projects', 'tasks', or 'workflows'
    const [projectTemplates, setProjectTemplates] = useState([]);
    const [taskTemplates, setTaskTemplates] = useState([]);
    const [workflowTemplates, setWorkflowTemplates] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showDeployModal, setShowDeployModal] = useState(false);
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);

    // Helper function to format estimated effort display
    const formatEstimatedEffort = (effort, metric) => {
      if (!effort) return '';
      const metricLabel = metric === 'minutes' ? 'min' : metric === 'hours' ? 'hr' : metric === 'days' ? 'd' : 'hr';
      return `${effort}${metricLabel}`;
    };

    // Helper to get trigger type display name
    const getTriggerTypeDisplay = (triggerType) => {
      const triggerNames = {
        'task_status_changed': 'Task Status Changed',
        'task_created': 'Task Created',
        'client_created': 'Client Created',
        'project_created': 'Project Created',
        'portal_access_granted': 'Portal Access Granted',
        'client_portal_submission': 'Client Portal Submission',
        'deliverable_uploaded': 'Deliverable Uploaded',
        'webhook': 'Webhook',
        'schedule': 'Schedule'
      };
      return triggerNames[triggerType] || triggerType;
    };

    useEffect(() => {
      fetchTemplates();
      fetchOrganizations();
    }, []);

    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('super_admin_token');
        
        // Fetch project, task, and workflow templates together
        const [projectsRes, tasksRes, workflowsRes] = await Promise.all([
          api.get(`/super-admin/template-library/projects`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          api.get(`/super-admin/template-library/tasks`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          api.get(`/super-admin/template-library/workflows`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setProjectTemplates(projectsRes.data.templates || []);
        setTaskTemplates(tasksRes.data.templates || []);
        setWorkflowTemplates(workflowsRes.data.templates || []);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem('super_admin_token');
        const response = await api.get(`/super-admin/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrganizations(response.data.organizations || []);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      }
    };

    const handleCreateTemplate = async (formData) => {
      try {
        setLoading(true);
        const token = localStorage.getItem('super_admin_token');
        const isEditing = formData.id ? true : false;
        
        let baseEndpoint;
        if (activeTab === 'projects') {
          baseEndpoint = `/super-admin/template-library/projects`;
        } else if (activeTab === 'tasks') {
          baseEndpoint = `/super-admin/template-library/tasks`;
        } else {
          baseEndpoint = `/super-admin/template-library/workflows`;
        }
        
        const endpoint = isEditing ? `${baseEndpoint}/${formData.id}` : baseEndpoint;
        
        if (isEditing) {
          await api.put(endpoint, formData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await api.post(endpoint, formData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        setShowCreateModal(false);
        setShowWorkflowModal(false);
        setSelectedTemplate(null);
        fetchTemplates();
        const typeLabel = activeTab === 'projects' ? 'Project' : activeTab === 'tasks' ? 'Task' : 'Workflow';
        alert(`${typeLabel} template ${isEditing ? 'updated' : 'created'} successfully!`);
      } catch (error) {
        alert(`Failed to ${formData.id ? 'update' : 'create'} template: ` + (error.response?.data?.detail || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    const handleDeployTemplate = async (teamId) => {
      try {
        const token = localStorage.getItem('super_admin_token');
        
        // Use different endpoint for workflows
        const deployEndpoint = activeTab === 'workflows' 
          ? `/super-admin/template-library/workflows/deploy`
          : `/super-admin/template-library/deploy`;
        
        await api.post(deployEndpoint, {
          team_id: teamId,
          template_id: selectedTemplate.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setShowDeployModal(false);
        setSelectedTemplate(null);
        
        const successMessage = activeTab === 'workflows'
          ? `Workflow deployed to organization successfully! (Deployed as DISABLED - admin must enable it)`
          : `Template deployed to organization successfully!`;
        alert(successMessage);
        
        // Small delay to ensure backend transaction completes before refresh
        setTimeout(() => {
          fetchTemplates();
        }, 500);
      } catch (error) {
        alert('Failed to deploy template: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    };

    const handleDeleteWorkflowTemplate = async (templateId) => {
      if (!window.confirm('Are you sure you want to delete this workflow template?')) return;
      
      try {
        const token = localStorage.getItem('super_admin_token');
        await api.delete(`/super-admin/template-library/workflows/${templateId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchTemplates();
        alert('Workflow template deleted successfully!');
      } catch (error) {
        alert('Failed to delete template: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    };

    const templates = activeTab === 'projects' ? projectTemplates : activeTab === 'tasks' ? taskTemplates : workflowTemplates;

    return (
      <div style={{ padding: '2rem', backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>Template Library</h2>
          <button
            onClick={() => activeTab === 'workflows' ? setShowWorkflowModal(true) : setShowCreateModal(true)}
            style={{ backgroundColor: '#7c3aed', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={16} /> Create {activeTab === 'projects' ? 'Project' : activeTab === 'tasks' ? 'Task' : 'Workflow'} Template
          </button>
        </div>

        <p style={{ color: '#4b5563', marginBottom: '1.5rem' }}>
          Create master templates that can be deployed to tenant organizations for faster onboarding
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setActiveTab('projects')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'projects' ? '600' : '400',
              color: activeTab === 'projects' ? '#7c3aed' : '#4b5563',
              borderBottom: activeTab === 'projects' ? '2px solid #7c3aed' : 'none'
            }}
          >
            Project Templates ({projectTemplates.length})
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'tasks' ? '600' : '400',
              color: activeTab === 'tasks' ? '#7c3aed' : '#4b5563',
              borderBottom: activeTab === 'tasks' ? '2px solid #7c3aed' : 'none'
            }}
          >
            Task Templates ({taskTemplates.length})
          </button>
          <button
            onClick={() => setActiveTab('workflows')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'workflows' ? '600' : '400',
              color: activeTab === 'workflows' ? '#7c3aed' : '#4b5563',
              borderBottom: activeTab === 'workflows' ? '2px solid #7c3aed' : 'none'
            }}
          >
            Workflow Templates ({workflowTemplates.length})
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {templates.map((template) => (
              <div key={template.id} style={{
                ...styles.card,
                padding: '1.5rem',
                cursor: 'pointer'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  {template.name}
                </h3>
                <p style={{ color: '#4b5563', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  {template.description || (activeTab === 'workflows' ? 'No description' : '')}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {/* Workflow trigger type badge */}
                  {activeTab === 'workflows' && template.trigger_type && (
                    <span style={{ ...styles.badge, backgroundColor: '#059669' }}>
                      {getTriggerTypeDisplay(template.trigger_type)}
                    </span>
                  )}
                  {/* Project/Task badges */}
                  {template.category && activeTab !== 'workflows' && (
                    <span style={{ ...styles.badge, backgroundColor: '#6B46C1' }}>
                      {template.category}
                    </span>
                  )}
                  {template.priority && activeTab === 'tasks' && (
                    <span style={{ ...styles.badge, backgroundColor: '#F59E0B' }}>
                      {template.priority}
                    </span>
                  )}
                  {template.task_count !== undefined && activeTab !== 'workflows' && (
                    <span style={{ ...styles.badge, backgroundColor: '#3B82F6' }}>
                      {activeTab === 'projects' 
                        ? `${template.task_count} tasks` 
                        : `${template.task_count} subtasks`}
                    </span>
                  )}
                  {template.estimated_duration_days && activeTab !== 'workflows' && (
                    <span style={{ ...styles.badge, backgroundColor: '#10B981' }}>
                      {template.estimated_duration_days} days
                    </span>
                  )}
                  {template.estimated_effort_value && activeTab === 'tasks' && (
                    <span style={{ ...styles.badge, backgroundColor: '#EC4899' }}>
                      {formatEstimatedEffort(template.estimated_effort_value, template.estimated_effort_unit)}
                    </span>
                  )}
                  {/* Workflow node count badge */}
                  {activeTab === 'workflows' && template.nodes && (
                    <span style={{ ...styles.badge, backgroundColor: '#3B82F6' }}>
                      {template.nodes.length} nodes
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setSelectedTemplate(template);
                      if (activeTab === 'workflows') {
                        setShowWorkflowModal(true);
                      } else {
                        setShowCreateModal(true);
                      }
                    }}
                    style={{
                      ...styles.button,
                      flex: 1,
                      backgroundColor: '#3B82F6',
                      fontWeight: '500'
                    }}
                  >
                    Edit
                  </button>
                  {activeTab === 'workflows' && (
                    <button
                      onClick={() => handleDeleteWorkflowTemplate(template.id)}
                      style={{
                        ...styles.button,
                        flex: 1,
                        backgroundColor: '#dc2626',
                        fontWeight: '500'
                      }}
                    >
                      Delete
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedTemplate(template);
                      setShowDeployModal(true);
                    }}
                    style={{
                      ...styles.button,
                      flex: 2,
                      background: 'linear-gradient(to right, #6B46C1, #EC4899)',
                      border: 'none',
                      fontWeight: '600'
                    }}
                    onMouseOver={(e) => e.target.style.background = 'linear-gradient(to right, #5B21B6, #DB2777)'}
                    onMouseOut={(e) => e.target.style.background = 'linear-gradient(to right, #6B46C1, #EC4899)'}
                  >
                    Deploy
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {templates.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#4b5563' }}>
            <Building2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <p>No {activeTab === 'workflows' ? 'workflow' : activeTab === 'projects' ? 'project' : 'task'} templates created yet</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Create your first template to get started
            </p>
          </div>
        )}

        {/* Create/Edit Template Modal (Projects/Tasks) */}
        {showCreateModal && activeTab !== 'workflows' && (
          <CreateTemplateModal
            type={activeTab}
            onClose={() => {
              setShowCreateModal(false);
              setSelectedTemplate(null);
            }}
            onCreate={handleCreateTemplate}
            taskTemplates={taskTemplates}
            editTemplate={selectedTemplate}
          />
        )}

        {/* Deploy Modal */}
        {showDeployModal && selectedTemplate && (
          <DeployTemplateModal
            template={selectedTemplate}
            organizations={organizations}
            onClose={() => setShowDeployModal(false)}
            onDeploy={handleDeployTemplate}
          />
        )}

        {/* Workflow Template Builder */}
        {showWorkflowModal && (
          <WorkflowTemplateBuilder
            template={selectedTemplate}
            onClose={() => {
              setShowWorkflowModal(false);
              setSelectedTemplate(null);
            }}
            onSave={() => {
              fetchTemplates();
            }}
          />
        )}
      </div>
    );
  };

  // Add Users view with impersonation
  const UsersView = () => {
    const [users, setUsers] = useState([]);
    const [showDeleted, setShowDeleted] = useState(false);
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
      if (showDeleted) {
        fetchDeletedUsers();
      } else {
        fetchUsers();
      }
    }, [searchTerm, showDeleted]);

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('super_admin_token');
        const url = searchTerm 
          ? `/super-admin/users?search=${encodeURIComponent(searchTerm)}`
          : `/super-admin/users`;
        
        const response = await api.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data.users || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchDeletedUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('super_admin_token');
        const response = await api.get(`/super-admin/deleted-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDeletedUsers(response.data.users || []);
      } catch (error) {
        console.error('Failed to fetch deleted users:', error);
      } finally {
        setLoading(false);
      }
    };

    const handleImpersonate = async (user) => {
      if (!window.confirm(`Impersonate ${user.name} (${user.email})?`)) {
        return;
      }

      try {
        const token = localStorage.getItem('super_admin_token');
        const response = await api.post(
          `/super-admin/impersonate/${user.id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` }}
        );

        // Store the impersonated user's token
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Open main app in new tab
        window.open('/', '_blank');
      } catch (error) {
        alert('Failed to impersonate user: ' + (error.response?.data?.detail || 'Unknown error'));
      }
    };

    const handleResetPassword = (user) => {
      setSelectedUser(user);
      setShowPasswordModal(true);
    };

    const handlePasswordModalClose = () => {
      setShowPasswordModal(false);
      setSelectedUser(null);
    };

    const handleDeleteUser = async (user) => {
      if (!window.confirm(`Are you sure you want to delete ${user.name} (${user.email})?\n\nThis user can be restored later from the Deleted Users section.`)) {
        return;
      }

      try {
        const token = localStorage.getItem('super_admin_token');
        await api.delete(`/super-admin/users/${user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        alert('User deleted successfully. You can restore them from the Deleted Users section.');
        fetchUsers(); // Refresh the list
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(`Failed to delete user: ${error.response?.data?.detail || error.message}`);
      }
    };

    const handleRestoreUser = async (user) => {
      if (!window.confirm(`Restore ${user.name} (${user.email})?`)) {
        return;
      }

      try {
        const token = localStorage.getItem('super_admin_token');
        await api.post(`/super-admin/users/${user.id}/restore`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        alert('User restored successfully!');
        fetchDeletedUsers(); // Refresh deleted users list
      } catch (error) {
        console.error('Error restoring user:', error);
        alert(`Failed to restore user: ${error.response?.data?.detail || error.message}`);
      }
    };

    const handleResendInvitation = async (user) => {
      const invitationId = (user.id || '').startsWith('invite:') ? user.id.slice('invite:'.length) : user.id;
      if (!invitationId) {
        alert('Missing invitation ID');
        return;
      }

      if (!window.confirm(`Resend invitation to ${user.email}?`)) {
        return;
      }

      try {
        const token = localStorage.getItem('super_admin_token');
        const response = await api.post(`/super-admin/invitations/${invitationId}/resend`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });

        alert(response?.data?.message || `Invitation resent to ${user.email}`);
        fetchUsers();
      } catch (error) {
        console.error('Error resending invitation:', error);
        alert(`Failed to resend invitation: ${error.response?.data?.detail || error.message}`);
      }
    };

    const handleResetInvite = async (user) => {
      if (!window.confirm(`Reset invite for ${user.name} (${user.email})?\n\nThis should be used for users who have never logged in.`)) {
        return;
      }

      try {
        const token = localStorage.getItem('super_admin_token');
        const response = await api.post(`/super-admin/users/${user.id}/reset-invite`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response?.data?.temporary_password) {
          const temporaryPassword = response.data.temporary_password;
          await copyToClipboard(temporaryPassword);
          alert(`${response?.data?.message || `Login provisioned for ${user.email}`}\n\nTemporary password copied to clipboard:\n${temporaryPassword}`);
        } else {
          alert(response?.data?.message || `Invitation sent to ${user.email}`);
        }
        fetchUsers();
      } catch (error) {
        console.error('Error resetting invite:', error);
        alert(`Failed to reset invite: ${error.response?.data?.detail || error.message}`);
      }
    };

    return (
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Users Management</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              style={{
                ...styles.actionButton,
                backgroundColor: showDeleted ? '#3b82f6' : '#6b7280',
                color: 'white',
                padding: '0.5rem 1rem'
              }}
            >
              <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
              {showDeleted ? 'Show Active Users' : 'Show Deleted Users'}
            </button>
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              ...styles.input,
              width: '300px'
            }}
          />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : (
          <div style={styles.table}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Team</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Last Login</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(showDeleted ? deletedUsers : users).map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={styles.td}>{user.name}</td>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>{user.team_name || 'No team'}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={styles.badge}>
                          {user.role || 'viewer'}
                        </span>
                        {user.pending && (
                          <span style={{ ...styles.badge, backgroundColor: '#f59e0b', color: 'white' }}>
                            Pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      {user.last_login ? (
                        <span style={{ fontSize: '0.875rem' }}>
                          {new Date(user.last_login).toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Never</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {showDeleted ? (
                          <>
                            <button
                              onClick={() => handleRestoreUser(user)}
                              style={{
                                ...styles.actionButton,
                                backgroundColor: '#10b981',
                                color: 'white'
                              }}
                              title="Restore User"
                            >
                              <Unlock size={14} /> Restore
                            </button>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
                              Deleted {new Date(user.deleted_at).toLocaleDateString()}
                              {user.deleted_by && ` by ${user.deleted_by}`}
                            </div>
                          </>
                        ) : user.pending ? (
                          <button
                            onClick={() => handleResendInvitation(user)}
                            style={{
                              ...styles.actionButton,
                              backgroundColor: '#8b5cf6',
                              color: 'white'
                            }}
                            title="Resend Invitation"
                          >
                            Resend Invite
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleImpersonate(user)}
                              style={{
                                ...styles.actionButton,
                                backgroundColor: '#3b82f6',
                                color: 'white'
                              }}
                            >
                              <Eye size={14} /> Login As
                            </button>

                            <details style={{ position: 'relative' }}>
                              <summary
                                style={{
                                  ...styles.actionButton,
                                  backgroundColor: '#6b7280',
                                  color: 'white',
                                  cursor: 'pointer',
                                  listStyle: 'none'
                                }}
                              >
                                Actions ▾
                              </summary>

                              <div
                                style={{
                                  position: 'absolute',
                                  right: 0,
                                  top: 'calc(100% + 6px)',
                                  minWidth: '170px',
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                                  padding: '0.35rem',
                                  zIndex: 20,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '0.35rem'
                                }}
                              >
                                <button
                                  onClick={() => handleResetPassword(user)}
                                  style={{ ...styles.actionButton, backgroundColor: '#f59e0b', color: 'white', width: '100%' }}
                                  title="Reset Password"
                                >
                                  <Lock size={14} /> Reset Password
                                </button>

                                {!user.last_login && (
                                  <button
                                    onClick={() => handleResetInvite(user)}
                                    style={{ ...styles.actionButton, backgroundColor: '#8b5cf6', color: 'white', width: '100%' }}
                                    title="Reset Invite"
                                  >
                                    Reset Invite
                                  </button>
                                )}

                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  style={{ ...styles.actionButton, backgroundColor: '#ef4444', color: 'white', width: '100%' }}
                                  title="Delete User"
                                >
                                  <Trash2 size={14} /> Delete
                                </button>
                              </div>
                            </details>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                No users found
              </div>
            )}
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordModal && selectedUser && (
          <PasswordResetModal 
            user={selectedUser}
            onClose={handlePasswordModalClose}
            onSuccess={() => {
              handlePasswordModalClose();
              alert('Password reset successfully!');
            }}
          />
        )}
      </div>
    );
  };

  // Password Reset Modal Component
  const PasswordResetModal = ({ user, onClose, onSuccess }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');

      // Validation
      if (!newPassword) {
        setError('Password is required');
        return;
      }

      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('super_admin_token');
        const response = await api.post(
          `/super-admin/users/${user.id}/reset-password`,
          { new_password: newPassword },
          { headers: { Authorization: `Bearer ${token}` }}
        );

        if (response.data.success) {
          onSuccess();
        }
      } catch (error) {
        setError(error.response?.data?.detail || 'Failed to reset password');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          padding: '2rem',
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>
              Reset Password for {user.name}
            </h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Email
              </label>
              <input
                type="text"
                value={user.email}
                disabled
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                New Password *
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Confirm Password *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#fee2e2',
                borderRadius: '0.375rem',
                marginBottom: '1rem',
                color: '#991b1b',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  backgroundColor: loading ? '#9ca3af' : '#f59e0b',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Create Template Modal Component
  const CreateTemplateModal = ({ type, onClose, onCreate, taskTemplates = [], editTemplate = null }) => {
    const [formData, setFormData] = useState(() => {
      // If editing, populate with existing template data
      if (editTemplate) {
        // Helper function to parse JSON fields
        const parseJsonField = (field) => {
          if (Array.isArray(field)) return field;
          if (typeof field === 'string') {
            try {
              const parsed = JSON.parse(field);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        };

        if (type === 'projects') {
          const defaultStages = parseJsonField(editTemplate.default_stages);
          return {
            id: editTemplate.id,
            name: editTemplate.name || '',
            description: editTemplate.description || '',
            category: editTemplate.category || '',
            estimated_duration_days: editTemplate.estimated_duration_days || '',
            default_stages: defaultStages,
            stages: defaultStages.length > 0 ? defaultStages : [
              { name: 'Planning', color: '#6B7280', order: 1 },
              { name: 'In Progress', color: '#3B82F6', order: 2 },
              { name: 'Review', color: '#F59E0B', order: 3 },
              { name: 'Complete', color: '#10B981', order: 4 }
            ],
            default_tasks: parseJsonField(editTemplate.default_tasks)
          };
        } else {
          return {
            id: editTemplate.id,
            name: editTemplate.name || '',
            description: editTemplate.description || '',
            category: editTemplate.category || '',
            priority: editTemplate.priority || 'Normal',
            estimated_effort_value: editTemplate.estimated_effort_value || '',
            estimated_effort_unit: editTemplate.estimated_effort_unit || 'hours',
            subtasks: parseJsonField(editTemplate.subtasks),
            comments: parseJsonField(editTemplate.comments),
            checklist_items: parseJsonField(editTemplate.checklist_items),
            tags: parseJsonField(editTemplate.tags),
            start_date_offset: editTemplate.start_date_offset || '',
            due_date_offset: editTemplate.due_date_offset || '',
            due_time: editTemplate.due_time || '',
            recurring_rule: editTemplate.recurring_rule || ''
          };
        }
      }
      
      // Otherwise, return empty form
      return type === 'projects' 
        ? {
            name: '',
            description: '',
            category: '',
            estimated_duration_days: '',
            stages: [
              { name: 'Planning', color: '#6B7280', order: 1 },
              { name: 'In Progress', color: '#3B82F6', order: 2 },
              { name: 'Review', color: '#F59E0B', order: 3 },
              { name: 'Complete', color: '#10B981', order: 4 }
            ],
            default_stages: [],
            default_tasks: []
          }
        : {
            name: '',
            description: '',
            category: '',
            priority: 'Normal',
            estimated_effort_value: '',
            estimated_effort_unit: 'hours',
            subtasks: [],
            comments: [],
            checklist_items: [],
            tags: [],
            start_date_offset: '',
            due_date_offset: '',
            due_time: '',
            recurring_rule: ''
          };
    });

    const [showSubtaskModal, setShowSubtaskModal] = useState(false);
    const [editingSubtaskIndex, setEditingSubtaskIndex] = useState(null);
    const [showRecurringModal, setShowRecurringModal] = useState(false);
    const [recurringDescription, setRecurringDescription] = useState('');
    const [showTaskSelector, setShowTaskSelector] = useState(false);
    
    // Helper function to format estimated effort display
    const formatEstimatedEffort = (effort, metric) => {
      if (!effort) return '';
      const metricLabel = metric === 'minutes' ? 'min' : metric === 'hours' ? 'hr' : metric === 'days' ? 'd' : 'hr';
      return `${effort}${metricLabel}`;
    };

    // Stage management for project templates
    const addStage = () => {
      setFormData({
        ...formData,
        stages: [...formData.stages, {
          name: '',
          color: '#6B7280',
          order: formData.stages.length + 1
        }]
      });
    };

    const removeStage = (index) => {
      const updatedStages = formData.stages.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        stages: updatedStages.map((stage, i) => ({ ...stage, order: i + 1 }))
      });
    };

    const updateStage = (index, field, value) => {
      const updatedStages = formData.stages.map((stage, i) => 
        i === index ? { ...stage, [field]: value } : stage
      );
      setFormData({
        ...formData,
        stages: updatedStages
      });
    };

    // Checklist management for task templates
    const addChecklistItem = () => {
      setFormData({
        ...formData,
        checklist_items: [...formData.checklist_items, { text: '', completed: false }]
      });
    };

    const removeChecklistItem = (index) => {
      setFormData({
        ...formData,
        checklist_items: formData.checklist_items.filter((_, i) => i !== index)
      });
    };

    const updateChecklistItem = (index, value) => {
      const updatedItems = formData.checklist_items.map((item, i) => 
        i === index ? { ...item, text: value } : item
      );
      setFormData({
        ...formData,
        checklist_items: updatedItems
      });
    };

    // Comment management for task templates
    const addComment = () => {
      setFormData({
        ...formData,
        comments: [...formData.comments, '']
      });
    };

    const removeComment = (index) => {
      setFormData({
        ...formData,
        comments: formData.comments.filter((_, i) => i !== index)
      });
    };

    const updateComment = (index, value) => {
      const updatedComments = formData.comments.map((comment, i) => 
        i === index ? value : comment
      );
      setFormData({
        ...formData,
        comments: updatedComments
      });
    };

    // Subtask management for task templates
    const handleAddSubtask = () => {
      setEditingSubtaskIndex(null);
      setShowSubtaskModal(true);
    };

    const handleEditSubtask = (index) => {
      setEditingSubtaskIndex(index);
      setShowSubtaskModal(true);
    };

    const handleSaveSubtask = (subtaskData) => {
      if (editingSubtaskIndex !== null) {
        const updatedSubtasks = formData.subtasks.map((s, i) => 
          i === editingSubtaskIndex ? subtaskData : s
        );
        setFormData({ ...formData, subtasks: updatedSubtasks });
      } else {
        setFormData({ ...formData, subtasks: [...formData.subtasks, subtaskData] });
      }
      setShowSubtaskModal(false);
      setEditingSubtaskIndex(null);
    };

    const handleRemoveSubtask = (index) => {
      setFormData({
        ...formData,
        subtasks: formData.subtasks.filter((_, i) => i !== index)
      });
    };

    // Generate recurring description from rule
    const generateRecurringDescription = (rule) => {
      if (!rule) return '';
      
      try {
        const config = JSON.parse(rule);
        const { frequency, interval, daysOfWeek, monthlyType, monthlyDate, monthlyWeek, monthlyDay, startTime, endTime, endType, endAfter, endBy } = config;
        
        const weekDays = [
          { value: 0, label: 'Sun' },
          { value: 1, label: 'Mon' },
          { value: 2, label: 'Tue' },
          { value: 3, label: 'Wed' },
          { value: 4, label: 'Thu' },
          { value: 5, label: 'Fri' },
          { value: 6, label: 'Sat' }
        ];
        
        let text = 'Repeats ';
        
        // Frequency
        if (frequency === 'daily') {
          text += interval === 1 ? 'daily' : `every ${interval} days`;
        } else if (frequency === 'weekly') {
          if (interval === 1) {
            if (daysOfWeek && daysOfWeek.length > 0) {
              const dayNames = daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ');
              text += `weekly on ${dayNames}`;
            } else {
              text += 'weekly';
            }
          } else {
            text += `every ${interval} weeks`;
          }
        } else if (frequency === 'monthly') {
          text += interval === 1 ? 'monthly' : `every ${interval} months`;
        } else if (frequency === 'yearly') {
          text += interval === 1 ? 'yearly' : `every ${interval} years`;
        }
        
        // Time
        if (startTime) {
          const [hours, minutes] = startTime.split(':');
          const hour12 = hours % 12 || 12;
          const ampm = hours < 12 ? 'AM' : 'PM';
          text += ` at ${hour12}:${minutes} ${ampm}`;
        }
        
        return text;
      } catch {
        return 'Custom recurring schedule';
      }
    };
    
    // Update recurring description when formData.recurring_rule changes
    React.useEffect(() => {
      if (formData.recurring_rule && !recurringDescription) {
        const desc = generateRecurringDescription(formData.recurring_rule);
        setRecurringDescription(desc);
      }
    }, [formData.recurring_rule]);
    
    // Recurring task management
    const handleRecurringSave = (rule, description) => {
      setFormData({ ...formData, recurring_rule: rule });
      setRecurringDescription(description);
      setShowRecurringModal(false);
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onCreate(formData);
    };

    return (
      <>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editTemplate ? 'Edit' : 'Create'} {type === 'projects' ? 'Project' : 'Task'} Template
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {type === 'projects' ? (
                <>
                  {/* PROJECT TEMPLATE FIELDS */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Template Name *
                      </label>
                      <InputWithVariablePicker
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="e.g., {{client.name}} - Website Redesign"
                        showVariablePicker={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use variables like {`{{client.name}}`} for dynamic project names
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Complexity</label>
                      <select
                        value={formData.complexity}
                        onChange={(e) => setFormData({...formData, complexity: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="Simple">Simple</option>
                        <option value="Medium">Medium</option>
                        <option value="Complex">Complex</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <InputWithVariablePicker
                      type="textarea"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Describe when to use this template... Use variable picker for dynamic content like organization name, client info, etc."
                      rows={3}
                      showVariablePicker={true}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use variable picker to insert dynamic fields like organization name, client info, contact details, etc.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="on-hold">On Hold</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Duration</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.estimated_duration}
                          onChange={(e) => setFormData({...formData, estimated_duration: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="0"
                        />
                        <select
                          value={formData.estimated_duration_metric || 'days'}
                          onChange={(e) => setFormData({...formData, estimated_duration_metric: e.target.value})}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date Offset</label>
                      <select
                        value={formData.due_date_offset || ''}
                        onChange={(e) => setFormData({...formData, due_date_offset: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">No default due date</option>
                        <option value="+1 day">1 day after start</option>
                        <option value="+3 days">3 days after start</option>
                        <option value="+1 week">1 week after start</option>
                        <option value="+2 weeks">2 weeks after start</option>
                        <option value="+1 month">1 month after start</option>
                        <option value="+2 months">2 months after start</option>
                        <option value="+3 months">3 months after start</option>
                        <option value="+6 months">6 months after start</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Due date will be calculated when project is created</p>
                    </div>
                  </div>

                  {/* Linked Task Templates */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Linked Task Templates
                        <span className="text-xs text-gray-500 block">Tasks that will be automatically created with this project</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowTaskSelector(true)}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                      >
                        {formData.default_tasks.length > 0 ? 'Edit Selection' : '+ Select Tasks'}
                      </button>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      {formData.default_tasks.length === 0 ? (
                        <div className="text-center py-6">
                          <CheckSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 mb-3">
                            No task templates linked yet
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowTaskSelector(true)}
                            className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                          >
                            Select Task Templates
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {formData.default_tasks.map((taskId) => {
                            const task = taskTemplates.find(t => t.id === taskId);
                            if (!task) return null;
                            return (
                              <div key={taskId} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">{task.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {task.priority} priority
                                    {task.estimated_effort_value && ` • ${formatEstimatedEffort(task.estimated_effort_value, task.estimated_effort_unit)}`}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      default_tasks: formData.default_tasks.filter(id => id !== taskId)
                                    });
                                  }}
                                  className="ml-3 p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-600">
                              <CheckSquare className="w-3 h-3 inline mr-1" />
                              {formData.default_tasks.length} task template{formData.default_tasks.length !== 1 ? 's' : ''} will be automatically created
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">Project Stages</label>
                      <button
                        type="button"
                        onClick={addStage}
                        className="text-sm text-purple-600 hover:text-purple-700"
                      >
                        + Add Stage
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.stages.map((stage, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={stage.name}
                            onChange={(e) => updateStage(index, 'name', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Stage name"
                          />
                          <input
                            type="color"
                            value={stage.color}
                            onChange={(e) => updateStage(index, 'color', e.target.value)}
                            className="w-12 h-10 border border-gray-300 rounded"
                          />
                          {formData.stages.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeStage(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* TASK TEMPLATE FIELDS */}
                  {/* Basic Information */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Template Name *
                        </label>
                        <InputWithVariablePicker
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="e.g., Setup - Initial Meeting with {{client.name}}"
                          showVariablePicker={true}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Use variables like {`{{client.name}}`} for dynamic task titles
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Default Stage</label>
                        <select
                          value={formData.default_stage}
                          onChange={(e) => setFormData({...formData, default_stage: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="To Do">To Do</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Review">Review</option>
                          <option value="Done">Done</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <InputWithVariablePicker
                        type="textarea"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        placeholder="Describe what this task involves... Use variable picker for dynamic content"
                        rows={3}
                        showVariablePicker={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use variable picker to insert dynamic fields like organization name, client info, contact details, etc.
                      </p>
                    </div>
                  </div>

                  {/* Classification */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Classification</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                        <select
                          value={formData.priority}
                          onChange={(e) => setFormData({...formData, priority: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="Low">Low</option>
                          <option value="Normal">Normal</option>
                          <option value="High">High</option>
                          <option value="Critical">Critical</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Effort</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={formData.estimated_effort_value}
                            onChange={(e) => setFormData({...formData, estimated_effort_value: e.target.value ? parseInt(e.target.value) : ''})}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="0"
                          />
                          <select
                            value={formData.estimated_effort_unit || 'hours'}
                            onChange={(e) => setFormData({...formData, estimated_effort_unit: e.target.value})}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Scheduling */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Default Scheduling</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date Offset</label>
                        <select
                          value={formData.start_date_offset}
                          onChange={(e) => setFormData({...formData, start_date_offset: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">Start immediately</option>
                          <option value="0 days">Same day as project start</option>
                          <option value="+1 day">1 day after project start</option>
                          <option value="+2 days">2 days after project start</option>
                          <option value="+3 days">3 days after project start</option>
                          <option value="+1 week">1 week after project start</option>
                          <option value="+2 weeks">2 weeks after project start</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">When task should start relative to project start</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date Offset</label>
                        <select
                          value={formData.due_date_offset}
                          onChange={(e) => setFormData({...formData, due_date_offset: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">No due date</option>
                          <option value="+1 day">1 day after start</option>
                          <option value="+2 days">2 days after start</option>
                          <option value="+3 days">3 days after start</option>
                          <option value="+1 week">1 week after start</option>
                          <option value="+2 weeks">2 weeks after start</option>
                          <option value="+1 month">1 month after start</option>
                          <option value="+3 business days">3 business days after start</option>
                          <option value="+5 business days">5 business days after start</option>
                          <option value="+10 business days">10 business days after start</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Due date calculated from task start or project start</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Time</label>
                        <input
                          type="time"
                          value={formData.due_time}
                          onChange={(e) => setFormData({...formData, due_time: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">Time of day for due date (optional)</p>
                      </div>
                    </div>
                  </div>

                  {/* Task Properties */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Task Properties</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="is_milestone"
                          checked={formData.is_milestone}
                          onChange={(e) => setFormData({...formData, is_milestone: e.target.checked})}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="is_milestone" className="text-sm font-medium text-gray-700">
                          Mark as milestone by default
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Recurring Task</label>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                id="is_recurring_template"
                                checked={!!formData.recurring_rule}
                                onChange={(e) => {
                                  if (!e.target.checked) {
                                    setFormData({...formData, recurring_rule: ''});
                                    setRecurringDescription('');
                                  } else {
                                    setShowRecurringModal(true);
                                  }
                                }}
                                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <label htmlFor="is_recurring_template" className="text-sm font-medium text-gray-700">
                                Recurring Template
                              </label>
                            </div>
                            
                            {formData.recurring_rule && (
                              <button
                                type="button"
                                onClick={() => setShowRecurringModal(true)}
                                className="flex items-center space-x-2 px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all text-sm"
                              >
                                <Repeat className="w-3 h-3" />
                                <span>Edit Schedule</span>
                              </button>
                            )}
                          </div>

                          {formData.recurring_rule && recurringDescription && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                              <p className="text-sm text-purple-800">
                                <Repeat className="w-4 h-4 inline mr-1" />
                                {recurringDescription}
                              </p>
                            </div>
                          )}

                          {formData.recurring_rule && !recurringDescription && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <p className="text-sm text-gray-600">
                                Advanced recurring schedule configured. Click "Edit Schedule" to modify.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tags - NOTE: Organizations will configure categories, but tags can be part of templates */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Tags</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default Tags</label>
                      <input
                        type="text"
                        value={formData.tags ? formData.tags.join(', ') : ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="tag1, tag2, tag3"
                      />
                      <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                    </div>
                  </div>

                  {/* Checklist Items */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">Default Checklist Items</h4>
                      <button
                        type="button"
                        onClick={addChecklistItem}
                        className="text-sm text-purple-600 hover:text-purple-700"
                      >
                        + Add Item
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.checklist_items.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={item.text}
                            onChange={(e) => updateChecklistItem(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Checklist item..."
                          />
                          <button
                            type="button"
                            onClick={() => removeChecklistItem(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {formData.checklist_items.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No checklist items yet. Click "Add Item" to get started.</p>
                      )}
                    </div>
                  </div>

                  {/* Subtasks */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Default Subtasks</h4>
                        <p className="text-xs text-gray-500 mt-1">Subtasks that will be automatically created when using this template</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddSubtask}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                      >
                        + Add Subtask
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.subtasks.map((subtask, index) => (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors cursor-pointer group"
                          onClick={() => handleEditSubtask(index)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h5 className="text-sm font-medium text-gray-900 truncate">
                                  {subtask.name || 'Untitled Subtask'}
                                </h5>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  subtask.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                                  subtask.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                                  subtask.priority === 'Normal' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {subtask.priority}
                                </span>
                              </div>
                              {subtask.description && (
                                <p className="text-sm text-gray-600 truncate mb-2">{subtask.description}</p>
                              )}
                              <div className="flex items-center space-x-3 text-xs text-gray-500">
                                {subtask.default_stage && (
                                  <span>Stage: {subtask.default_stage}</span>
                                )}
                                {subtask.estimated_effort_value && (
                                  <span>Effort: {subtask.estimated_effort_value} {subtask.estimated_effort_unit || 'hours'}</span>
                                )}
                                {subtask.tags && subtask.tags.length > 0 && (
                                  <span>{subtask.tags.length} tag{subtask.tags.length > 1 ? 's' : ''}</span>
                                )}
                                {subtask.checklist_items && subtask.checklist_items.length > 0 && (
                                  <span>{subtask.checklist_items.length} checklist item{subtask.checklist_items.length > 1 ? 's' : ''}</span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSubtask(index);
                              }}
                              className="opacity-0 group-hover:opacity-100 p-2 text-red-600 hover:bg-red-50 rounded transition-opacity"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {formData.subtasks.length === 0 && (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <CheckSquare className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">No subtasks yet</p>
                          <p className="text-xs text-gray-400 mt-1">Click "Add Subtask" to create a template subtask</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* System Comments */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">System Comments</h4>
                        <p className="text-xs text-gray-500 mt-1">Comments that will be automatically added as system messages when task is created. Use variable picker to insert dynamic fields.</p>
                      </div>
                      <button
                        type="button"
                        onClick={addComment}
                        className="text-sm text-purple-600 hover:text-purple-700"
                      >
                        + Add Comment
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.comments.map((comment, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <div className="flex-1">
                            <InputWithVariablePicker
                              type="textarea"
                              value={comment}
                              onChange={(e) => updateComment(index, e.target.value)}
                              placeholder="System comment text... Use variable picker for dynamic content"
                              rows={2}
                              showVariablePicker={true}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeComment(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {formData.comments.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No system comments yet. Click "Add Comment" to get started.</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md"
              >
                <Save className="w-4 h-4 mr-2" />
                {editTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>

        {/* Subtask Template Modal */}
        {showSubtaskModal && (
          <SubtaskTemplateModal
            isOpen={showSubtaskModal}
            onClose={() => {
              setShowSubtaskModal(false);
              setEditingSubtaskIndex(null);
            }}
            onSave={handleSaveSubtask}
            subtask={editingSubtaskIndex !== null ? formData.subtasks[editingSubtaskIndex] : null}
            teamMembers={[]}
          />
        )}

        {/* Recurring Task Modal */}
        {showRecurringModal && (
          <RecurringTaskModal
            isOpen={showRecurringModal}
            onClose={() => setShowRecurringModal(false)}
            onSave={handleRecurringSave}
            initialRule={formData.recurring_rule}
          />
        )}

        {/* Task Template Selector Modal */}
        {showTaskSelector && type === 'projects' && (
          <TaskTemplateSelector
            isOpen={showTaskSelector}
            onClose={() => setShowTaskSelector(false)}
            taskTemplates={taskTemplates}
            selectedTaskIds={formData.default_tasks}
            onSave={(selectedIds) => {
              setFormData({
                ...formData,
                default_tasks: selectedIds
              });
            }}
          />
        )}
      </>
    );
  };

  // Deploy Template Modal Component
  const DeployTemplateModal = ({ template, organizations, onClose, onDeploy }) => {
    const [selectedTeamId, setSelectedTeamId] = useState('');

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          padding: '2rem',
          width: '90%',
          maxWidth: '500px'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
            Deploy Template
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            Deploy "{template.name}" to an organization
          </p>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Select Organization *
            </label>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              style={styles.input}
              required
            >
              <option value="">-- Select Organization --</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.organization_name} ({org.total_users} members)
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{ ...styles.button, backgroundColor: '#6b7280' }}
            >
              Cancel
            </button>
            <button
              onClick={() => selectedTeamId && onDeploy(selectedTeamId)}
              disabled={!selectedTeamId}
              style={{
                ...styles.button,
                backgroundColor: '#7c3aed',
                opacity: selectedTeamId ? 1 : 0.5
              }}
            >
              Deploy Template
            </button>
          </div>
        </div>
      </div>
    );
  };

export default SuperAdminDashboard;
